/**
 * Le déboursé à l'écran : ses totaux, son arbre, et le vis-à-vis de la
 * contre-étude.
 *
 * **Ce que cet écran sert à faire** : d'abord ouvrir le classeur du déboursé à
 * côté et vérifier que les chiffres sont les mêmes (PRD §5.1) ; ensuite
 * retravailler ce chiffrage — moins d'heures, un prix négocié — et voir
 * immédiatement ce que ça change (PRD §4.2).
 *
 * Quatre choix découlent de là :
 *
 *  - **le n° de ligne du tableur est affiché sur chaque ligne.** C'est ce qui
 *    permet de pointer un écart dans le classeur au lieu de le chercher ;
 *  - **le montant annoncé par le fichier reste affiché** à côté du recalculé.
 *    Le cacher donnerait un écran qui a toujours raison ;
 *  - **aucune valeur n'est recalculée ici.** Après chaque modification, tout est
 *    rechargé depuis le serveur, qui a fait tourner le moteur. Recalculer côté
 *    navigateur créerait un second calcul à tenir d'accord avec le premier — et
 *    l'écart entre les deux ne se verrait qu'au rechargement de la page ;
 *  - **le devis reste visible en permanence** pendant qu'on modifie. Une
 *    contre-étude qui n'affiche que son propre chiffre ne se contre-étudie pas.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import {
  formaterMontant,
  formaterNombre,
  LIBELLE_A_NOMMER,
  STATUT_LIGNE_INITIAL,
  STATUTS_LIGNE,
  TOLERANCE_HEURE,
} from "@planif/moteur";
import type {
  DepoVue,
  Generique,
  EnteteVersion,
  NoeudVue,
  OuvrageVue,
  RessourceVue,
  GroupeAdmin,
} from "@planif/moteur";
import { Acheminement } from "./Acheminement";
import type { ChangementAcheminement } from "./Acheminement";
import { StockDeLaLigne } from "./StockDeLaLigne";
import { chargerGrille, chargerTarifsManquants, chercherArticles } from "./api";
import { codeDuChampArticle } from "./codeArticle";
import { vitesseDeDefilement } from "./defilementPendantGlissement";
import { poserLeBloc } from "./poserLeBloc";
import type { MoitieSurvolee, Pose } from "./poserLeBloc";
// `Provenance` est aussi le nom du composant de cette page : l'alias évite la collision
// sans renommer le type côté API, où il porte bien son nom.
import type {
  ArticleTarif,
  ArticleTrouve,
  Carriere,
  MotifSansTarif,
  Provenance as ProvenanceLigne,
} from "./api";
import { variationsDOrdonnancement } from "./ordonnancement";
import type { Ordonnancement } from "./ordonnancement";
import { estProposee, groupesDeRemplacement } from "./remplacement";

/* ---------------------------------------------------------------------------
 * Retrouver, dans le devis, ce qui correspond à la ligne affichée
 * ------------------------------------------------------------------------- */

/**
 * L'appariement se fait par NUMÉRO de poste et RANG de ligne, comme partout
 * ailleurs dans le produit. Les identifiants techniques changent d'une version
 * copiée à l'autre ; « la ressource 3 de l'ouvrage 4.4.7 » désigne la même chose
 * dans le devis et dans la contre-étude.
 */
function indexerPostes(racines: readonly NoeudVue[]): Map<string, NoeudVue> {
  const index = new Map<string, NoeudVue>();
  const descendre = (noeuds: readonly NoeudVue[]): void => {
    for (const noeud of noeuds) {
      index.set(noeud.numero, noeud);
      if (noeud.nature === "chapitre") descendre(noeud.enfants);
    }
  };
  descendre(racines);
  return index;
}

/** L'écart d'un montant à sa référence, ou `null` s'il n'y a rien à comparer. */
function ecartAuDevis(courant: string, reference: string | undefined): number | null {
  if (reference === undefined) return null;
  const delta = Number(courant) - Number(reference);
  // Le Number() n'est utilisé QUE pour décider d'une couleur et d'un signe :
  // jamais pour afficher une valeur, jamais pour recalculer quoi que ce soit.
  // La valeur affichée reste la chaîne exacte venue du serveur.
  return Number.isFinite(delta) ? delta : null;
}

function classeEcart(delta: number | null): string {
  if (delta === null || Math.abs(delta) < 0.005) return "";
  // Vert = gain, rouge = dépassement (PRD §4.2).
  return delta < 0 ? "gain" : "depassement";
}

/**
 * Cette version porte-t-elle au moins une ligne que le conducteur a travaillée ?
 *
 * Sert à lire l'écart au fichier, et c'est le seul critère qui distingue les deux
 * situations que rien ne séparait à l'écran :
 *
 *  - **aucune ligne touchée et pourtant un écart** → l'import ou le moteur a un
 *    problème, il faut le crier ;
 *  - **des lignes touchées et un écart** → c'est la conséquence attendue du
 *    travail. Le déboursé recalculé n'a plus aucune raison d'égaler ce que le
 *    fichier annonce : une contre-étude qui ne s'en écarterait jamais ne servirait
 *    à rien.
 *
 * *Le 11/08/2026, la copie de travail d'APO250010 s'affichait « non conforme, 6
 * postes en écart dont 2 inexpliqués » parce qu'un étirement de barre avait
 * légitimement changé des heures. Ça se lit comme une panne de l'outil, et ça
 * masque du même coup un vrai écart, qui devient un rouge parmi d'autres.*
 */
function noeudRetravaille(noeud: NoeudVue): boolean {
  return noeud.nature === "chapitre"
    ? noeud.enfants.some(noeudRetravaille)
    : noeud.ressources.some((r) => r.statut !== STATUT_LIGNE_INITIAL);
}

function versionRetravaillee(racines: readonly NoeudVue[]): boolean {
  return racines.some(noeudRetravaille);
}

/**
 * La couleur de la colonne « Écart fichier », sur un chapitre ou un ouvrage.
 *
 * *Corrigé sur signalement de Chris, 19/08/2026 : sur APO250010, un poste recalculé à 1 794,62 €
 * contre 1 938,62 € annoncés s'affichait en ROUGE. « Nous parlons de charges de contre-étude » :
 * une charge qui baisse est un gain, et elle doit se lire comme les autres — en vert.*
 *
 * Mais la couleur ne se déduit **pas** du seul signe, sinon cette colonne perdrait sa raison
 * d'être. Elle existe pour dire qu'une ligne ne boucle pas avec le fichier d'origine, et sur un
 * poste que personne n'a touché, c'est un défaut d'import ou de moteur — **rouge quel que soit
 * son sens**, y compris quand il est négatif. Le colorier au signe ferait passer un vrai défaut
 * pour une bonne nouvelle : exactement l'état faux silencieux qu'on s'interdit.
 *
 * La distinction se fait donc poste par poste, et non sur la version entière : dans une
 * contre-étude retravaillée, les postes intacts doivent continuer de crier.
 */
export function tonEcartFichier(
  aUneAnomalie: boolean,
  retravaille: boolean,
  ecart: string,
): string {
  if (!aUneAnomalie) return "";
  if (!retravaille) return "en-ecart";
  const delta = Number(ecart);
  return classeEcart(Number.isFinite(delta) ? delta : null);
}

function classeEcartFichier(noeud: NoeudVue): string {
  return tonEcartFichier(noeud.anomalie !== null, noeudRetravaille(noeud), noeud.ecart);
}

/**
 * Un écart signé. `neutre` le montre sans couleur : un déplacement de bloc n'est ni un gain
 * ni un dépassement, il change seulement de chapitre ce qui a été chiffré.
 */
function Delta({ valeur, neutre = false }: { valeur: number | null; neutre?: boolean }): JSX.Element {
  if (valeur === null) return <span className="neutre">—</span>;
  if (Math.abs(valeur) < 0.005) return <span className="neutre">=</span>;
  const signe = valeur > 0 ? "+" : "−";
  return (
    <span className={neutre ? "" : classeEcart(valeur)}>
      {signe}
      {formaterMontant(Math.abs(valeur).toFixed(2))}
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * Le cartouche des totaux
 * ------------------------------------------------------------------------- */

function Total({
  libelle,
  valeur,
  precision,
  ton,
}: {
  libelle: string;
  valeur: string;
  precision?: string;
  ton?: "conforme" | "ecart" | "gain" | "depassement";
}): JSX.Element {
  return (
    <div className={`total${ton === undefined ? "" : ` total--${ton}`}`}>
      <span className="total__libelle">{libelle}</span>
      <span className="total__valeur">{valeur}</span>
      {precision !== undefined && <span className="total__precision">{precision}</span>}
    </div>
  );
}

function Totaux({
  depo,
  entete,
  reference,
  libelleReference,
}: {
  depo: DepoVue;
  entete: EnteteVersion;
  reference: DepoVue | null;
  libelleReference: string | null;
}): JSX.Element {
  const { totaux } = depo;
  const gain =
    reference === null
      ? null
      : ecartAuDevis(totaux.deboursRecalcule, reference.totaux.deboursRecalcule);

  /**
   * L'écart au fichier est-il un DÉFAUT, ou la conséquence du travail ?
   *
   * Sur le devis figé — celui qui n'a pas de référence — c'est le contrôle de
   * fidélité de l'import, et il doit crier. Sur une copie de travail déjà
   * retravaillée, l'écart est attendu : c'est le gain de la contre-étude, mesuré
   * plus haut face au devis. Garder l'alerte rouge dans ce cas coûte deux fois —
   * elle fait douter d'un outil qui a raison, et elle noie l'écart réel du jour où
   * il y en aura un.
   */
  const retravaillee = reference !== null && versionRetravaillee(depo.racines);

  /**
   * De combien la vente retenue s'écarte du montant du marché.
   *
   * `null` quand le fichier n'annonce pas de marché : il n'y a alors rien à
   * composer, et afficher « +35 487,87 € d'écart » ferait passer une donnée
   * absente pour une anomalie.
   */
  const ecartDeVente =
    entete.montantMarcheHT === null
      ? null
      : Number(totaux.prixVenteTotal) - Number(entete.montantMarcheHT);

  /**
   * Le COEFFICIENT DE MARGE : ce que le marché rapporte pour un euro de déboursé.
   *
   * *Demande de Chris, 16/09/2026 : « rajouter le coefficient de marge, c'est-à-dire montant du
   * marché / déboursé recalculé ».*
   *
   * **Le déboursé RECALCULÉ.** C'est la grandeur qui bouge quand on travaille la contre-étude :
   * le coefficient d'une copie de travail suit donc le chiffrage qu'on est en train de faire, et
   * celui du devis figé dit où l'on partait.
   *
   * **Les deux termes portent le même périmètre**, et c'est ce qui rend le rapport honnête : le
   * déboursé recalculé et le montant du marché excluent tous deux les options non retenues — la
   * règle du produit depuis le 09/08/2026. Diviser un marché complet par un déboursé amputé
   * gonflerait le coefficient sans que rien ne le dise.
   *
   * `null` dans deux cas qui ne se confondent pas, et que la carte nomme séparément : le marché
   * n'est pas renseigné, ou le déboursé est nul — une division par zéro rendrait `Infinity`, qui
   * s'afficherait comme un coefficient.
   */
  const coefficientDeMarge =
    entete.montantMarcheHT === null || Number(totaux.deboursRecalcule) === 0
      ? null
      : Number(entete.montantMarcheHT) / Number(totaux.deboursRecalcule);

  /**
   * LE MÊME COEFFICIENT SUR LE DÉBOURSÉ ANNONCÉ PAR LE FICHIER.
   *
   * *Demande de Chris, 22/09/2026 : « je voudrais créer une autre carte coefficient de marge =
   * montant du marché divisé par déboursé annoncé par le fichier ».*
   *
   * **Ce que la seconde carte apprend que la première ne dit pas** : le coefficient tel que le
   * classeur le portait, avant qu'on ait retravaillé quoi que ce soit. La première suit le
   * chiffrage en cours, la seconde reste le point de départ — et c'est leur ÉCART qui mesure ce
   * que la contre-étude a gagné, sans avoir à comparer deux versions. *J'avais écrit le
   * 16/09/2026 que l'annoncé « donnerait le même nombre sur les deux versions et n'apprendrait
   * rien ». C'était faux dans les deux sens : il bouge dès qu'un poste est écarté, et surtout
   * c'est précisément sa stabilité face au chiffrage retravaillé qui en fait un repère.*
   *
   * **Le périmètre est le même que celui du recalculé**, donc le rapport reste honnête :
   * `deboursAnnonce` et `deboursRecalcule` se totalisent tous deux sur les postes RETENUS
   * (`comptees`, moteur `depo.ts`). Écarter une option retire son montant annoncé comme son
   * montant recalculé, des deux côtés de la division.
   *
   * Les mêmes deux `null` que ci-dessus, pour les mêmes raisons.
   */
  const coefficientDeMargeAnnonce =
    entete.montantMarcheHT === null || Number(totaux.deboursAnnonce) === 0
      ? null
      : Number(entete.montantMarcheHT) / Number(totaux.deboursAnnonce);

  return (
    <section className="totaux">
      <Total
        libelle="Déboursé recalculé"
        valeur={`${formaterMontant(totaux.deboursRecalcule)} €`}
        precision="ligne à ligne, par le moteur"
      />

      {reference !== null && (
        <Total
          libelle={libelleReference ?? "Référence"}
          valeur={`${formaterMontant(reference.totaux.deboursRecalcule)} €`}
          precision="le chiffrage d'origine, figé"
        />
      )}

      {/*
        L'étiquette suit le SIGNE, elle ne se contente pas de changer de couleur.
        *Demande de Chris, 19/08/2026 : « si le chiffre était en plus il devrait apparaître en
        rouge et on devrait écrire perte sur la contre-étude ».* Un cartouche qui annonce
        « Gain » au-dessus d'un nombre positif oblige à relire le signe pour comprendre qu'on a
        renchéri — et sur un écran qu'on parcourt, personne ne relit un signe.
      */}
      {reference !== null && (
        <Total
          libelle={
            gain !== null && gain >= 0.005 ? "Perte sur la contre-étude" : "Gain de la contre-étude"
          }
          valeur={
            gain === null || Math.abs(gain) < 0.005
              ? "aucun pour l'instant"
              : `${gain < 0 ? "−" : "+"}${formaterMontant(Math.abs(gain).toFixed(2))} €`
          }
          precision={
            gain === null || Math.abs(gain) < 0.005
              ? undefined
              : gain < 0
                ? "moins cher que le devis"
                : "plus cher que le devis"
          }
          ton={gain === null || Math.abs(gain) < 0.005 ? undefined : gain < 0 ? "gain" : "depassement"}
        />
      )}

      <Total
        libelle="Déboursé annoncé par le fichier"
        valeur={`${formaterMontant(totaux.deboursAnnonce)} €`}
        precision={
          totaux.postesEcartes > 0
            ? "somme des postes de premier niveau retenus"
            : "somme des postes de premier niveau"
        }
      />
      <Total
        libelle={retravaillee && !totaux.conforme ? "Écart au fichier d'origine" : "Écart au fichier"}
        valeur={`${formaterMontant(totaux.ecartTotal)} €`}
        precision={
          totaux.conforme
            ? "dans la tolérance du centime"
            : retravaillee
              ? `attendu : ${totaux.anomalies} poste(s) ont été retravaillés — le gain se lit face au devis, ci-dessus`
              : `${totaux.anomalies} poste(s) en écart, dont ${totaux.anomaliesInexpliquees} inexpliqué(s)`
        }
        // Pas de rouge sur une version qu'on a le droit d'avoir changée : le rouge
        // est réservé à ce qui n'a pas d'explication. Le jour où une version NON
        // retravaillée s'écarte du fichier, il redevient le bon signal.
        ton={totaux.conforme ? "conforme" : retravaillee ? undefined : "ecart"}
      />

      {/* La fidélité de l'import se prouve sur le devis figé, pas sur la copie :
          c'est lui qui doit tomber au centime, et il ne bouge plus. L'afficher ici
          évite d'aller changer de version pour savoir si le fichier a bien été lu. */}
      {reference !== null && (
        <Total
          libelle="Fidélité de l'import"
          valeur={reference.totaux.conforme ? "vérifiée" : "en défaut"}
          precision={
            reference.totaux.conforme
              ? `le ${libelleReference ?? "devis figé"} retombe au centime sur le fichier`
              : `${reference.totaux.anomalies} poste(s) du ${libelleReference ?? "devis figé"} ne retombent pas — à traiter avant de conclure`
          }
          ton={reference.totaux.conforme ? "conforme" : "ecart"}
        />
      )}
      {totaux.signalements > 0 && (
        <Total
          libelle="Postes à vérifier"
          valeur={`${totaux.signalements} ouvrage(s)`}
          // Ces postes-là ne créent aucun écart : ils sont chiffrés et pèsent
          // zéro. Sans cette case, rien à l'écran ne les distinguerait d'un
          // ouvrage sain — c'est tout l'intérêt de la signaler.
          precision="chiffrés mais à quantité nulle — ils ne pèsent dans aucun total"
          ton="ecart"
        />
      )}

      {/* L'avancement de l'affectation des familles de travaux — le contrôle
          bloquant du PRD §4.3, affiché en permanence comme le classeur du métier
          le fait. Ce qui compte n'est pas le taux atteint mais de savoir OÙ sont
          les manquantes : le libellé les nomme, et la case devient un signal
          d'écart tant que le compte n'est pas plein. */}
      {totaux.groupes.total > 0 && (
        <Total
          libelle="Tâches affectées à une famille"
          valeur={`${totaux.groupes.affectees} / ${totaux.groupes.total}`}
          precision={
            totaux.groupes.complet
              ? totaux.groupes.desaccords > 0
                ? `${totaux.groupes.desaccords} à vérifier — une autre famille y est proposée`
                : "toutes affectées : le planning peut être établi"
              : `${totaux.groupes.total - totaux.groupes.affectees} sans famille — à affecter avant de planifier`
          }
          ton={totaux.groupes.complet ? "conforme" : "ecart"}
        />
      )}

      {totaux.postesEcartes > 0 && (
        <Total
          libelle="Postes écartés du chiffrage"
          valeur={`${totaux.postesEcartes} poste(s)`}
          // Les deux montants ensemble, et pas seulement les charges : c'est le
          // couple qui a manqué à Chris le 09/08/2026 — une option retirée des
          // coûts mais laissée dans le chiffre d'affaires fabrique une marge qui
          // n'existe pas.
          precision={
            `${formaterMontant(totaux.deboursEcarte)} € de charges et ` +
            `${formaterMontant(totaux.prixVenteEcarte)} € de vente, hors chiffrage`
          }
        />
      )}

      <Total
        libelle="Prix de vente (somme des ouvrages)"
        valeur={`${formaterMontant(totaux.prixVenteTotal)} €`}
        precision={
          totaux.postesEcartes > 0 ? "sur le périmètre retenu, options écartées" : undefined
        }
      />
      {/*
        **Le SECOND contrôle du produit, et le seul qui distingue deux écartements.**

        *Règle posée par Chris le 10/08/2026 : le prix de vente retenu doit composer
        le montant du marché. Mesuré le 12/08 sur un cas réel : écarter les trois
        postes d'une option, ou écarter le chapitre entier, annulent TOUS DEUX
        l'écart de déboursé — le premier est juste, le second fait disparaître
        3 182,36 € de vente qui, elle, était bien vendue. Sans ce chiffre à l'écran,
        les deux gestes se ressemblent et le mauvais passe pour bon.*

        Le ton ne devient jamais rouge : un écart qui subsiste après écartement est
        une remise négociée, pas une erreur — mesuré, 5 des 7 déboursés réels
        tombent au centime, les 2 autres gardent 0,66 % et 0,75 %.
      */}
      <Total
        libelle="Montant du marché"
        valeur={
          entete.montantMarcheHT === null
            ? "non renseigné"
            : `${formaterMontant(entete.montantMarcheHT)} €`
        }
        precision={
          ecartDeVente === null
            ? "prix négocié — il n'a pas à égaler la somme des ventes"
            : Math.abs(ecartDeVente) < 0.005
              ? "composé au centime par les ouvrages retenus : le périmètre tombe juste"
              : `${ecartDeVente < 0 ? "−" : "+"}${formaterMontant(Math.abs(ecartDeVente).toFixed(2))} € ` +
                `(${(Math.abs(ecartDeVente) / Number(entete.montantMarcheHT ?? 1) * 100).toFixed(2)} %) ` +
                "face à la vente retenue — remise négociée, ou périmètre à revoir"
        }
        ton={ecartDeVente !== null && Math.abs(ecartDeVente) < 0.005 ? "conforme" : undefined}
      />
      <Total
        libelle="Heures du chantier"
        valeur={`${formaterNombre(totaux.heures, 2)} h`}
        precision={
          reference === null
            ? "toutes ressources dont l'unité est l'heure"
            : `devis : ${formaterNombre(reference.totaux.heures, 2)} h`
        }
        ton={
          reference === null || reference.totaux.heures === totaux.heures
            ? undefined
            : Number(totaux.heures) < Number(reference.totaux.heures)
              ? "gain"
              : "depassement"
        }
      />

      <Total
        libelle="Contenu"
        valeur={`${totaux.chapitres} chapitres · ${totaux.ouvrages} ouvrages`}
        precision={`${totaux.ressources} lignes de ressources`}
      />

      {/*
        **Le coefficient de marge** — *demande de Chris, 16/09/2026.*

        Le calcul est écrit en toutes lettres dans la carte, avec ses deux montants : c'est la
        même règle que le contrôle du transport, où « 80 T = 17 tours de 20 min » a remplacé un
        total seul. Un coefficient de 1,32 ne dit pas lequel des deux termes corriger ; les deux
        montants sous les yeux, si.

        **Aucun ton.** Un coefficient n'a pas de seuil dans ce produit — décider ici qu'en deçà
        de 1,20 il faut du rouge inventerait une règle de gestion que personne n'a posée, et
        elle ferait autorité le jour où quelqu'un la lirait.
      */}
      <Total
        libelle="Coefficient de marge (déboursé recalculé)"
        valeur={
          coefficientDeMarge === null ? "incalculable" : formaterNombre(coefficientDeMarge.toFixed(3), 3)
        }
        precision={
          entete.montantMarcheHT === null
            ? "le montant du marché n'est pas renseigné"
            : Number(totaux.deboursRecalcule) === 0
              ? "le déboursé recalculé est nul — rien à rapporter au marché"
              : `${formaterMontant(entete.montantMarcheHT)} € de marché ÷ ` +
                `${formaterMontant(totaux.deboursRecalcule)} € de déboursé recalculé` +
                (totaux.postesEcartes > 0 ? ", options écartées de part et d'autre" : "")
        }
      />

      {/*
        **Le coefficient au fichier** — *demande de Chris, 22/09/2026.*

        Les deux cartes se lisent l'une à côté de l'autre, et les DEUX libellés nomment leur
        déboursé : laisser « Coefficient de marge » tout court sur la première aurait obligé à
        lire la ligne de précision pour savoir laquelle on regarde. Le vocabulaire est celui des
        deux cartes de déboursé plus haut dans le même écran, pas un vocabulaire neuf.

        **Aucun ton ici non plus**, et pas davantage sur l'écart entre les deux : un coefficient
        n'a pas de seuil dans ce produit, et une contre-étude qui fait baisser le coefficient
        n'est pas une anomalie — elle peut avoir corrigé un chiffrage d'origine trop optimiste.
      */}
      <Total
        libelle="Coefficient de marge (déboursé annoncé)"
        valeur={
          coefficientDeMargeAnnonce === null
            ? "incalculable"
            : formaterNombre(coefficientDeMargeAnnonce.toFixed(3), 3)
        }
        precision={
          entete.montantMarcheHT === null
            ? "le montant du marché n'est pas renseigné"
            : Number(totaux.deboursAnnonce) === 0
              ? "le déboursé annoncé par le fichier est nul — rien à rapporter au marché"
              : `${formaterMontant(entete.montantMarcheHT)} € de marché ÷ ` +
                `${formaterMontant(totaux.deboursAnnonce)} € de déboursé annoncé par le fichier` +
                (totaux.postesEcartes > 0 ? ", options écartées de part et d'autre" : "")
        }
      />
    </section>
  );
}

/**
 * Le besoin en heures par ressource — ce que le plan de charge consommera.
 *
 * PRD §4.7 : « comparaison des totaux d'heures par ressource générique — besoin
 * de 80 h/jour de chef de chantier face au disponible réel ». Ce tableau est la
 * première moitié de cette comparaison ; l'autre viendra du référentiel des
 * disponibilités.
 *
 * Le regroupement est fait par CODE, sans qualifier ce que le code désigne :
 * décider ici qu'un code est de la main-d'œuvre ou du matériel ferait entrer
 * dans l'écran une règle propre à l'ERP d'un client.
 */
/**
 * Ce que chaque famille de travaux pèse sur le chantier.
 *
 * C'est la vue que le classeur du métier produit — heures et déboursé sec par
 * regroupement — et que l'application ne savait pas faire. Elle ne coûte rien à
 * calculer une fois les tâches affectées, et elle répond à une question que le
 * déboursé poste par poste ne permet pas de poser : *où part l'argent de ce
 * chantier ?*
 *
 * Le total est rappelé en pied et **comparé au déboursé du chantier**. L'écart est
 * la mesure de ce qui n'est pas encore affecté : il tombe à zéro quand le compteur
 * atteint 100 %. Un écart qui subsisterait à 100 % serait, lui, un vrai défaut —
 * c'est un contrôle qui ne coûte qu'une ligne et qui ne peut pas se tromper.
 */
function ParFamille({ depo }: { depo: DepoVue }): JSX.Element | null {
  const [ouvert, setOuvert] = useState(false);
  const { parGroupe, groupes } = depo.totaux;
  if (parGroupe.length === 0) return null;

  const somme = (extraire: (g: (typeof parGroupe)[number]) => string): number =>
    parGroupe.reduce((total, g) => total + Number(extraire(g)), 0);

  const debourseAffecte = somme((g) => g.debourse);
  const manquant = Number(depo.totaux.deboursRecalcule) - debourseAffecte;

  return (
    <section className="besoins">
      <button
        type="button"
        className="besoins__titre"
        onClick={() => {
          setOuvert(!ouvert);
        }}
        aria-expanded={ouvert}
      >
        {ouvert ? "▾" : "▸"} Par famille de travaux — {parGroupe.length} famille(s) utilisée(s)
        {!groupes.complet && `, ${groupes.total - groupes.affectees} tâche(s) sans famille`}
      </button>

      {ouvert && (
        <table className="ressources">
          <thead>
            <tr>
              <th>Code</th>
              <th>Famille</th>
              <th className="col-nombre">Tâches</th>
              <th className="col-nombre">Heures</th>
              <th className="col-nombre">Déboursé</th>
              <th className="col-nombre">Vente</th>
            </tr>
          </thead>
          <tbody>
            {parGroupe.map((g) => (
              <tr key={g.id}>
                <td className="code">
                  <span className="groupe-poste">
                    <span
                      className="groupe-pastille"
                      style={{
                        backgroundColor: g.couleur ?? "transparent",
                        borderColor: g.couleur ?? "var(--trait)",
                      }}
                    />
                    {g.code}
                  </span>
                </td>
                <td>{g.libelle}</td>
                <td className="col-nombre">{g.taches}</td>
                <td className="col-nombre heures">
                  {formaterNombre(g.heures, 2)} h
                  {/*
                    LE DÉTAIL DEMANDÉ PAR CHRIS LE 27/08/2026, sous le total et non à côté :
                    « préciser à l'intérieur de la famille dont h _MOICDTEENG = , dont
                    _MOICDC = , juste pour les trois ressources citées ». Une colonne par
                    code aurait élargi le tableau d'autant à chaque code coché ; sous le
                    total, la liste s'allonge sans jamais déformer la grille.

                    Les codes viennent du référentiel (`detail_synthese`, 050) : le moteur
                    les rend dans l'ordre demandé, y compris à zéro. Un code qui
                    n'apparaîtrait que dans les familles où il travaille obligerait à relire
                    l'intitulé à chaque ligne.
                  */}
                  {g.heuresParCode.map((h) => (
                    <span key={h.code} className="dont" title={h.libelle}>
                      dont {h.code} : {formaterNombre(h.heures, 2)} h
                    </span>
                  ))}
                </td>
                <td className="col-nombre montant">{formaterMontant(g.debourse)}</td>
                <td className="col-nombre vente">{formaterMontant(g.prixVente)}</td>
              </tr>
            ))}
            <tr className="besoins__total">
              <td colSpan={2}>Total affecté</td>
              <td className="col-nombre">{groupes.affectees}</td>
              <td className="col-nombre heures">{formaterNombre(somme((g) => g.heures).toFixed(4), 2)} h</td>
              <td className="col-nombre montant">{formaterMontant(debourseAffecte.toFixed(2))}</td>
              <td className="col-nombre vente">
                {formaterMontant(somme((g) => g.prixVente).toFixed(2))}
              </td>
            </tr>
            {Math.abs(manquant) >= 0.005 && (
              <tr className="besoins__total">
                <td colSpan={2} className="en-ecart">
                  Pas encore affecté
                </td>
                <td className="col-nombre en-ecart">{groupes.total - groupes.affectees}</td>
                <td />
                <td className="col-nombre en-ecart">{formaterMontant(manquant.toFixed(2))}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Besoins({
  depo,
  reference,
}: {
  depo: DepoVue;
  reference: DepoVue | null;
}): JSX.Element | null {
  const [ouvert, setOuvert] = useState(false);
  if (depo.totaux.besoins.length === 0) return null;

  const heuresDevis = new Map<string, string>();
  for (const b of reference?.totaux.besoins ?? []) heuresDevis.set(b.code, b.heures);

  return (
    <section className="besoins">
      <button
        type="button"
        className="besoins__titre"
        onClick={() => {
          setOuvert(!ouvert);
        }}
        aria-expanded={ouvert}
      >
        {ouvert ? "▾" : "▸"} Besoin en heures par ressource —{" "}
        {depo.totaux.besoins.length} ressources, {formaterNombre(depo.totaux.heures, 2)} h
      </button>

      {ouvert && (
        <table className="ressources">
          <thead>
            <tr>
              <th>Code</th>
              <th>Désignation</th>
              <th className="col-nombre">Lignes</th>
              {reference !== null && <th className="col-nombre col-devis">Heures devis</th>}
              <th className="col-nombre">Heures</th>
              <th className="col-nombre">Δ</th>
              <th className="col-nombre">Coût</th>
            </tr>
          </thead>
          <tbody>
            {depo.totaux.besoins.map((besoin) => {
              const avant = heuresDevis.get(besoin.code);
              const delta =
                avant === undefined ? null : Number(besoin.heures) - Number(avant);
              return (
                <tr key={besoin.code}>
                  <td className="code">{besoin.code}</td>
                  <td>{besoin.designation}</td>
                  <td className="col-nombre">{besoin.lignes}</td>
                  {reference !== null && (
                    <td className="col-nombre col-devis">
                      {avant === undefined ? "—" : formaterNombre(avant, 2)}
                    </td>
                  )}
                  <td className="col-nombre">{formaterNombre(besoin.heures, 2)}</td>
                  <td className="col-nombre">
                    {delta === null || Math.abs(delta) < 0.005 ? (
                      <span className="neutre">=</span>
                    ) : (
                      <span className={delta < 0 ? "gain" : "depassement"}>
                        {delta > 0 ? "+" : "−"}
                        {formaterNombre(Math.abs(delta).toFixed(4), 2)} h
                      </span>
                    )}
                  </td>
                  <td className="col-nombre">{formaterMontant(besoin.cout)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * La saisie d'une valeur
 * ------------------------------------------------------------------------- */

/**
 * Un champ qui n'envoie sa valeur qu'une fois la saisie terminée.
 *
 * Envoyer à chaque frappe produirait un appel par caractère — et « 18 » passerait
 * par « 1 », qui est une valeur parfaitement plausible et qui serait écrite en
 * base. La validation se fait donc à la sortie du champ ou sur Entrée, et Échap
 * abandonne.
 *
 * La valeur d'origine est réaffichée si la saisie est refusée : un champ qui
 * garde une valeur que le serveur n'a pas acceptée ment sur l'état réel.
 */
function Saisie({
  valeur,
  decimales,
  suffixe,
  modifiable,
  modifiee,
  enAttente,
  onValider,
}: {
  valeur: string;
  decimales: number;
  suffixe?: string;
  modifiable: boolean;
  modifiee: boolean;
  enAttente: boolean;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const affichee = formaterNombre(valeur, decimales);
  const [brouillon, setBrouillon] = useState<string | null>(null);

  if (!modifiable) {
    return (
      <span className={modifiee ? "valeur-modifiee" : undefined}>
        {affichee}
        {suffixe}
      </span>
    );
  }

  return (
    <input
      className={`saisie${modifiee ? " valeur-modifiee" : ""}${enAttente ? " saisie--attente" : ""}`}
      value={brouillon ?? affichee}
      disabled={enAttente}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        // À la prise de focus on montre la valeur BRUTE, sans séparateur de
        // milliers : « 1 735,0000 » n'est pas un nombre qu'on peut corriger au
        // clavier, et le réécrire entièrement à chaque retouche est pénible.
        setBrouillon(Number(valeur).toString());
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== "") onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/* ---------------------------------------------------------------------------
 * Le libellé d'un poste
 * ------------------------------------------------------------------------- */

/**
 * Le libellé d'un poste : du texte, sauf sur un poste créé dans la contre-étude.
 *
 * *Demande de Chris du 21/08/2026 : « il faudra que je puisse donner le libellé sur la ligne 3
 * et 3.1 ».* Le champ n'apparaît que là où le geste existe — un poste du devis porte le
 * libellé du classeur, et c'est à lui que la contre-étude se compare poste par poste.
 *
 * Même règle de validation que `Saisie` : à la sortie du champ ou sur Entrée, jamais à chaque
 * frappe. Et le libellé de départ, « À nommer », est effacé à la prise de focus plutôt qu'à
 * corriger au clavier — c'est un marqueur, pas une valeur.
 *
 * Le bouton de suppression vit ici et non dans une colonne à lui : il ne concerne qu'un
 * chapitre créé, et une colonne de gestes vide sur 131 postes du déboursé est exactement ce
 * que le métier a fait retirer des cases à cocher.
 */
function Designation({
  noeud,
  modifiable,
  enAttente,
  onModifier,
}: {
  noeud: NoeudVue;
  modifiable: boolean;
  enAttente: boolean;
  onModifier: (modification: Modification) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);
  const cree = noeud.origine === "ajoute";

  if (!cree || !modifiable) return <>{noeud.designation}</>;

  return (
    <span className="designation-creee">
      <input
        className={`saisie saisie--libelle${enAttente ? " saisie--attente" : ""}`}
        value={brouillon ?? noeud.designation}
        disabled={enAttente}
        placeholder="Libellé du poste"
        aria-label={`Libellé du poste ${noeud.numero}`}
        onChange={(e) => {
          setBrouillon(e.target.value);
        }}
        onFocus={(e) => {
          if (noeud.designation === LIBELLE_A_NOMMER) setBrouillon("");
          else e.target.select();
        }}
        onBlur={() => {
          const saisi = brouillon;
          setBrouillon(null);
          // Un libellé vide n'est pas envoyé : le serveur le refuserait, et un poste sans
          // désignation ne se retrouve pas dans une liste de 131 lignes.
          if (saisi !== null && saisi.trim() !== "" && saisi.trim() !== noeud.designation) {
            onModifier({ poste: noeud.numero, rang: 0, libelle: saisi.trim() });
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setBrouillon(null);
            e.currentTarget.blur();
          }
        }}
      />
      {noeud.nature === "chapitre" && (
        <button
          type="button"
          className="lien"
          disabled={enAttente}
          title="Retirer ce chapitre créé, et son sous-chapitre. Refusé s'il porte des lignes ou un planning."
          onClick={() => {
            onModifier({ poste: noeud.numero, rang: 0, supprimerChapitre: true });
          }}
        >
          Retirer
        </button>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------------------
 * Les lignes de l'arbre
 * ------------------------------------------------------------------------- */

/**
 * Le nombre de colonnes de l'arbre — la case à cocher comprise.
 *
 * Il sert aux lignes qui s'étendent sur toute la largeur (l'anomalie, le détail
 * d'un ouvrage). Le laisser diverger de l'en-tête ne casse rien visiblement : le
 * tableau se décale d'une colonne, et on cherche longtemps pourquoi.
 */
const COLONNES = 16;

/**
 * De quoi ouvrir le panneau de stock sur les lignes qui le méritent (070).
 *
 * Les codes viennent du **serveur** : c'est lui qui juge si un code est celui d'un article, avec
 * le préfixe paramétré. Une règle réécrite ici se tromperait le jour où le métier désignera ses
 * articles autrement — et le piège du 07/09/2026 est proche : à l'import, le type `ART` est posé
 * sur tout code absent du référentiel générique, matériaux de carrière compris.
 */
export type ContexteStock = {
  readonly chantierId: string;
  readonly codes: ReadonlySet<string>;
};

/**
 * LA GRILLE DES DEUX TABLEAUX, déclarée une fois et posée sur les deux.
 *
 * *Signalé par Chris le 24/08/2026 : « en contre-étude, tout déplier — les colonnes ne sont
 * pas alignées, par exemple la désignation est décalée », puis « aligne aussi les colonnes de
 * droite ».*
 *
 * Le détail d'un ouvrage est un tableau IMBRIQUÉ dans une cellule de l'arbre : ses colonnes
 * se calculaient pour lui seul, sans aucun rapport avec celles du dessus. Deux tableaux qui
 * se suivent verticalement et dont les traits ne tombent pas au même endroit se lisent comme
 * deux tableaux, alors que c'est un seul déboursé.
 *
 * Les deux ont donc les MÊMES QUINZE COLONNES, aux mêmes largeurs. Le détail n'a pas quinze
 * choses à montrer — il en a quatorze —, d'où une colonne laissée vide plutôt qu'un décalage :
 * une cellule vide ne coûte rien à lire, un décalage coûte la lecture de tout le tableau.
 *
 * L'ORDRE DU DÉTAIL A ÉTÉ REFAIT POUR ÇA, et c'est le vrai changement. Chaque colonne tombe
 * sous celle de l'arbre qui dit la même chose :
 *
 * | l'arbre        | le détail      |
 * |----------------|----------------|
 * | ✓              | ↑ ↓            |
 * | Ligne          | Ligne          |
 * | N°             | Code           |
 * | Désignation    | Désignation    |
 * | Groupe         | Provenance     |
 * | Unité          | Un.            |
 * | Quantité       | Quantité       |
 * | Heures         | Qté devis      |
 * | Devis          | PU devis       |
 * | Vente devis    | Prix unitaire  |
 * | Contre-étude   | Coût           |
 * | Vente          | —              |
 * | Δ              | Δ              |
 * | Annoncé        | État           |
 * | Écart fichier  | Retirer        |
 *
 * Les largeurs sont en POURCENTAGES : le tableau tient toujours dans la fenêtre, et les deux
 * grilles restent identiques quelle que soit sa taille — ce qu'aucune largeur en `rem` ne
 * garantit. Chacune est celle du plus large des deux contenus : la colonne « Groupe » est
 * dimensionnée par la provenance du détail (une case à cocher et une liste de carrières), pas
 * par sa pastille.
 */
const LARGEURS_COLONNES = [
  "3%", // ✓ / les flèches d'ordre
  "3%", // Ligne
  "7%", // N° / Code — porte aussi le retrait de l'arbre et le chevron
  // Désignation (5) et Groupe (1) ont cédé 6 points le 24/09/2026 à « Var. ordonnancement » : le
  // tableau doit tenir en plein écran sans défiler de côté (demande de Chris).
  "11%", // Désignation — porte aussi la note de transport posé
  "10%", // Groupe / Provenance — élargie le 24/08 : la liste des carrières y tient
  "4%", // Unité
  // L'ORDRE DES QUATRE SUIVANTES A CHANGÉ LE 27/08/2026, à la demande de Chris : la
  // quantité descend après le PU du devis, pour venir CONTRE le prix unitaire. Les deux
  // colonnes sur lesquelles le conducteur écrit sont ainsi côte à côte, après les deux
  // valeurs du devis qu'elles corrigent — et le fond vert les désigne d'un bloc.
  "6%", // Heures / Qté devis
  "7%", // Devis / PU devis
  "5%", // Quantité
  "7%", // Vente devis / Prix unitaire
  "7%", // Contre-étude / Coût
  "7%", // Vente
  "5%", // Δ — le chiffrage seul depuis le 24/09/2026
  "6%", // Var. ordonnancement / —
  "6%", // Annoncé / État
  "6%", // Écart fichier / Retirer
] as const;

/** La grille, posée à l'identique sur l'arbre et sur le détail. */
function ColonnesCalees(): JSX.Element {
  return (
    <colgroup>
      {LARGEURS_COLONNES.map((largeur, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <col key={`${largeur}-${String(index)}`} style={{ width: largeur }} />
      ))}
    </colgroup>
  );
}

/**
 * La famille de travaux d'un poste : sa pastille, et de quoi la changer.
 *
 * Trois états se distinguent à l'œil, et cette distinction est le cœur du contrôle
 * bloquant du PRD §4.3 :
 *
 *  - **posé** — pastille pleine : une décision du conducteur ;
 *  - **hérité ou proposé** — pastille creuse : l'application a rempli, personne n'a
 *    encore confirmé. Sans cette nuance, un chantier entièrement colorié par le
 *    dictionnaire paraîtrait entièrement validé ;
 *  - **rien** — un tiret, et le poste apparaît dans le filtre des non affectés.
 *
 * Le **désaccord** porte un point d'exclamation : la mémoire ou le dictionnaire
 * proposaient autre chose que l'héritage retenu. C'est le poste à regarder — sur le
 * classeur du métier, c'est exactement l'unique exception du seul chapitre mixte.
 */
function ChoixGroupe({
  noeud,
  groupes,
  modifiable,
  enAttente,
  onAffecter,
}: {
  noeud: NoeudVue;
  groupes: readonly GroupeAdmin[];
  modifiable: boolean;
  enAttente: boolean;
  onAffecter: (groupeId: string | null) => void;
}): JSX.Element {
  const groupe = noeud.groupe;
  const pose = groupe?.source === "affecte";
  const couleur = groupe?.couleur ?? "transparent";

  /**
   * Un grand titre qui contient des sous-chapitres ne porte pas de regroupement.
   *
   * *Demande de Chris, 19/08/2026.* On n'affiche alors **ni pastille ni liste de choix** :
   * un menu qui propose un classement que le serveur refuse se lit comme une panne, et une
   * pastille vide se lit comme un poste à traiter — or il n'y a rien à faire ici.
   */
  if (noeud.nature === "chapitre" && !noeud.accepteUnRegroupement) {
    return (
      <span
        className="groupe-poste groupe-poste--sans-objet"
        title={
          "Ce titre regroupe des sous-chapitres : il ne porte pas de famille de travaux. " +
          "Le code se pose sur ses sous-chapitres, et descend sur leurs tâches."
        }
      >
        —
      </span>
    );
  }

  const titre =
    groupe === null
      ? "Aucune famille de travaux — à affecter avant de planifier"
      : `${groupe.code} ${groupe.libelle}` +
        (pose
          ? " — posé ici"
          : groupe.source === "herite"
            ? " — hérité du chapitre"
            : groupe.source === "memoire"
              ? " — proposé : ce libellé a déjà été classé ainsi"
              : " — proposé par un mot-clé") +
        (groupe.desaccord === null ? "" : `. Attention : ${groupe.desaccord} proposé ici.`);

  return (
    <span className="groupe-poste" title={titre}>
      <span
        className={`groupe-pastille${pose ? "" : " groupe-pastille--propose"}`}
        style={{ backgroundColor: pose ? couleur : "transparent", borderColor: couleur }}
      />
      {modifiable ? (
        <select
          className="groupe-choix"
          value={groupe?.source === "affecte" ? groupe.id : ""}
          disabled={enAttente}
          aria-label={`Famille de travaux du poste ${noeud.numero}`}
          onChange={(e) => {
            onAffecter(e.target.value === "" ? null : e.target.value);
          }}
        >
          {/* La valeur vide n'est pas « pas de groupe » mais « hérite » : c'est
              l'état juste d'un chapitre contractuel, dont les enfants portent la
              nature de travaux. */}
          <option value="">{groupe === null ? "—" : `(${groupe.code})`}</option>
          {groupes
            .filter((g) => g.actif)
            .map((g) => (
              <option key={g.id} value={g.id}>
                {g.code} {g.libelle}
              </option>
            ))}
        </select>
      ) : (
        <span className="groupe-code">{groupe?.code ?? "—"}</span>
      )}
      {groupe?.desaccord !== null && groupe !== null && (
        <span className="groupe-desaccord" title={`${groupe.desaccord} proposé ici`}>
          !
        </span>
      )}
    </span>
  );
}

function Anomalie({ noeud }: { noeud: NoeudVue }): JSX.Element | null {
  if (noeud.anomalie === null) return null;
  return (
    <tr className="ligne-anomalie">
      <td />
      <td colSpan={COLONNES - 1}>
        <span className={`fanion fanion--${noeud.anomalie.nature}`}>
          {/* Un signalement n'est pas un écart : le fanion doit dire lequel des
              deux c'est, sinon on cherche une différence de montant qui n'existe
              pas. */}
          {noeud.anomalie.nature === "ouvrage_quantite_nulle" ? "à vérifier" : "écart"}
        </span>{" "}
        {noeud.anomalie.explication}
      </td>
    </tr>
  );
}

export type Modification = {
  readonly poste: string;
  /** `0` = ajout d'une ligne au poste ; sinon le rang de la ligne modifiée. */
  readonly rang: number;
  readonly quantite?: string;
  readonly prixUnitaire?: string;
  readonly supprimee?: boolean;
  readonly remplacerPar?: string;
  /** Renseigné pour un ajout : la ressource choisie au référentiel. */
  readonly ajouterCode?: string;
  /** Réordonner : seule la position change, jamais l'identité de la ligne. */
  readonly deplacer?: "haut" | "bas";
  /**
   * Compter le POSTE, ou l'écarter du chiffrage.
   *
   * Porte sur le poste entier — chapitre ou ouvrage — et non sur une ligne :
   * c'est la case à cocher des options et des variantes.
   */
  readonly retenu?: boolean;
  /**
   * Affecter une famille de travaux au POSTE, ou la retirer avec `null`.
   *
   * `null` remet le poste en heritage — l etat juste d un chapitre contractuel,
   * dont les enfants portent la nature de travaux, pas lui.
   */
  readonly groupeId?: string | null;
  /**
   * D'où vient ce matériau, et qui l'apporte.
   *
   * Un geste à part des autres — il ne déplace aucun euro et ne marque pas la
   * ligne comme retravaillée. La carrière ne se saisit que si `duGroupe` est
   * vrai ; décocher l'efface. **Chaque champ absent reste inchangé** : le panneau
   * enregistre ce qu'il affiche, pas ce qu'il ignore.
   */
  readonly provenance?: Partial<Omit<ProvenanceLigne, "carriereCode">>;
  /**
   * Nommer un poste **créé** dans la contre-étude (migration 048).
   *
   * Le serveur refuse sur un poste du devis : son libellé est celui du classeur, et le
   * vis-à-vis se lit face à lui. L'écran n'offre donc le champ que là où le geste existe.
   */
  readonly libelle?: string;
  /**
   * Créer un chapitre vierge à la suite du dernier, avec son sous-chapitre.
   *
   * *Demande de Chris du 21/08/2026 : « quand une CE a oublié un chapitre je dois pouvoir le
   * créer de zéro directement dans la copie modifiable ».* Rien à porter — ni numéro, ni
   * libellé : le numéro se déduit de l'existant, le libellé se saisit sur la trame.
   */
  readonly creerChapitre?: true;
  /** Retirer un chapitre créé par erreur. Refusé s'il porte des lignes ou un planning. */
  readonly supprimerChapitre?: true;
};

/**
 * Le détail d'un ouvrage : ses lignes de ressources, face à celles du devis.
 *
 * Le temps unitaire n'est pas affiché ici, la quantité l'est : c'est en heures
 * que le conducteur raisonne (« 40 h de chef prévues, j'en mets 20 », §4.2). Le
 * temps unitaire reste consultable en survolant la quantité — c'est la valeur
 * brute du fichier, avec ses vingt décimales, celle qu'on retrouve dans la
 * cellule du tableur.
 */
function Ressources({
  ouvrage,
  reference,
  modifiable,
  enAttente,
  generiques,
  articlesTarif,
  carrieres,
  sansTarif,
  stock,
  onModifier,
}: {
  ouvrage: OuvrageVue;
  reference: OuvrageVue | null;
  modifiable: boolean;
  enAttente: string | null;
  generiques: readonly Generique[];
  /** Les matériaux de la grille tarifaire, proposés à l'ajout après le matériel. */
  articlesTarif: readonly ArticleTarif[];
  carrieres: readonly Carriere[];
  /** Les lignes « du groupe » dont la grille ne donne aucun prix, par `poste|rang`. */
  sansTarif: ReadonlyMap<string, MotifSansTarif>;
  /** Où le panneau de stock s'ouvre, et pour quel chantier (070). */
  stock: ContexteStock;
  onModifier: (modification: Modification) => void;
}): JSX.Element {
  /*
    UNE seule ligne ouverte à la fois, désignée par `poste|rang`.

    Le panneau lit le stock à son ouverture : en laisser plusieurs ouverts multiplierait les
    lectures sans qu'aucune serve — on ne réserve qu'un article à la fois — et deux panneaux du
    même article afficheraient bientôt deux nombres différents.
  */
  const [stockOuvert, setStockOuvert] = useState<string | null>(null);
  // L'appariement se fait sur le rang D'ORIGINE, pas sur la position : une
  // ligne deplacee doit continuer de se comparer a la meme ligne du devis.
  // Confondre les deux afficherait la quantite d'une autre ressource, sans que
  // rien ne le signale.
  const parOrigine = new Map<number, RessourceVue>();
  for (const r of reference?.ressources ?? []) {
    if (r.rangOrigine !== null) parOrigine.set(r.rangOrigine, r);
  }

  return (
    <tr className="ligne-detail">
      {/* UNE seule cellule, sur toute la largeur, et sans retrait à gauche : c'est ce qui
          permet au tableau imbriqué de commencer exactement où commence celui du dessus.
          La cellule vide qui précédait le décalait d'une colonne, et le retrait de 1,5 rem
          d'autant — deux raisons pour que rien ne tombe jamais en face. */}
      <td colSpan={COLONNES}>
        <table className="ressources">
          <ColonnesCalees />
          <thead>
            <tr>
              {/* Les flèches en tête de ligne, avant le n° de tableur : c'est
                  là qu'on cherche une commande d'ordre, et elles y forment une
                  colonne verticale qu'on suit du regard. */}
              <th className="col-gestes" />
              <th className="col-ligne">Ligne</th>
              <th>Code</th>
              <th>Désignation</th>
              {/*
                La provenance ne concerne que ce qui se LIVRE — les tonnes, les
                mètres cubes, les unités. Une ligne en heures est de la main-d'œuvre
                ou une machine : elle ne vient d'aucune carrière.

                Elle vient ICI, sous le groupe de l'arbre : c'est la colonne large
                qui suit la désignation des deux côtés, et la seule assez large pour
                une case à cocher suivie d'une liste de carrières.
              */}
              <th title="Matériau produit par le groupe, et carrière de provenance">
                Provenance
              </th>
              <th>Un.</th>
              <th className="col-nombre col-devis">Qté devis</th>
              <th className="col-nombre col-devis">PU devis</th>
              {/*
                LES DEUX COLONNES SUR LESQUELLES ON ÉCRIT, CÔTE À CÔTE ET TEINTÉES.

                *« Trouver un moyen de bien identifier la colonne QUANTITÉ et la colonne
                PRIX UNITAIRE pour que le conducteur voie très rapidement que c'est sur ces
                deux colonnes qu'il peut changer les prix et les coûts »* (Chris,
                27/08/2026). Chacune vient contre la valeur du devis qu'elle corrige : la
                quantité après « Qté devis », le prix après « PU devis ».

                Le fond n'apparaît QUE sur une version modifiable — sur un devis figé ou une
                version validée, rien ne se saisit, et une colonne qui se présenterait comme
                ouverte serait un mensonge. C'est le pendant de `col-devis`, qui met en
                retrait ce qui se consulte : ici on met en avant ce qui s'écrit.
              */}
              <th className={`col-nombre${modifiable ? " col-saisie" : ""}`}>Quantité</th>
              <th className={`col-nombre${modifiable ? " col-saisie" : ""}`}>Prix unitaire</th>
              <th className="col-nombre">Coût</th>
              {/* La colonne « Vente » de l'arbre : une ressource n'a pas de prix de
                  vente, seul l'ouvrage en a un. Vide, et non supprimée — c'est ce
                  qui garde les deux grilles superposables. */}
              <th />
              <th className="col-nombre">Δ</th>
              {/* « Var. ordonnancement » : une ressource ne change pas de chapitre seule. */}
              <th />
              <th>État</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ouvrage.ressources.map((ressource, index) => {
              const origine =
                ressource.rangOrigine === null ? undefined : parOrigine.get(ressource.rangOrigine);
              const delta = ecartAuDevis(ressource.cout, origine?.cout);
              const cle = `${ouvrage.numero}#${ressource.rang}`;
              // Les deux colonnes qu'on saisit, teintées seulement là où l'on peut écrire.
              const colonneSaisie = `col-nombre${modifiable ? " col-saisie" : ""}`;
              const quantiteModifiee =
                origine !== undefined && origine.quantiteReelle !== ressource.quantiteReelle;
              const prixModifie =
                origine !== undefined && origine.prixUnitaire !== ressource.prixUnitaire;

              const retiree = ressource.statut === "supprime";

              /*
                LE STOCK NE S'OUVRE QUE SUR UN ARTICLE COCHÉ « DU GROUPE ».

                *« Si groupe est cliqué sur un article, cela veut dire que cela proviendra du
                stock »* (Chris). Les deux conditions comptent : sans le code d'article, un
                matériau de carrière proposerait de se réserver au magasin ; sans « du groupe »,
                le bouton s'afficherait sur les articles achetés au fournisseur, dont le magasin
                n'a rien à sortir.

                Une ligne retirée du chiffrage n'en propose pas non plus : il n'y a plus rien à
                approvisionner pour elle.
              */
              const surStock =
                !retiree && ressource.duGroupe && stock.codes.has(ressource.code);

              return (
                <Fragment key={cle}>
                <tr className={`${classeEcart(delta)}${retiree ? " retiree" : ""}`}>
                  <td className="col-gestes">
                    {modifiable && (
                      <>
                        <button
                          type="button"
                          className="fleche"
                          disabled={enAttente === cle || index === 0}
                          title="Monter cette ligne"
                          aria-label={`Monter la ligne ${ressource.code}`}
                          onClick={() => {
                            onModifier({
                              poste: ouvrage.numero,
                              rang: ressource.rang,
                              deplacer: "haut",
                            });
                          }}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="fleche"
                          disabled={enAttente === cle || index === ouvrage.ressources.length - 1}
                          title="Descendre cette ligne"
                          aria-label={`Descendre la ligne ${ressource.code}`}
                          onClick={() => {
                            onModifier({
                              poste: ouvrage.numero,
                              rang: ressource.rang,
                              deplacer: "bas",
                            });
                          }}
                        >
                          ↓
                        </button>
                      </>
                    )}
                  </td>
                  <td className="col-ligne">
                    {/* Une ligne ajoutée par le conducteur ne vient d'aucune
                        ligne du classeur : lui inventer un numéro ferait croire
                        à une correspondance qui n'existe pas. */}
                    {ressource.ligneTableur > 0 ? ressource.ligneTableur : "—"}
                  </td>
                  <td className="code">
                    {modifiable ? (
                      <ChoixRessource
                        valeur={ressource.code}
                        generiques={generiques}
                        articlesTarif={articlesTarif}
                        enAttente={enAttente === cle}
                        onChoisir={(code) => {
                          onModifier({ poste: ouvrage.numero, rang: ressource.rang, remplacerPar: code });
                        }}
                      />
                    ) : (
                      ressource.code
                    )}
                    {ressource.codeOrigine !== null && (
                      <span className="origine" title="Ressource d'origine, avant remplacement">
                        {" ← "}
                        {ressource.codeOrigine}
                      </span>
                    )}
                  </td>
                  <td>
                    {ressource.designation}
                    {/* LA NOTE DE TRANSPORT VIT ICI, SOUS LA DÉSIGNATION.
                        Elle était collée à la quantité, dans une colonne de 5 % : depuis que
                        la grille est fixe, elle y était illisible — signalé par Chris le
                        24/08/2026, « on ne voit pas le message en rouge ». Sous la
                        désignation, elle a toute la largeur de la colonne la plus large, et
                        la ligne grandit d'autant. */}
                    <HeuresPosees ressource={ressource} />
                  </td>
                  <td className="provenance">
                    <Provenance
                      ressource={ressource}
                      carrieres={carrieres}
                      motifSansTarif={
                        sansTarif.get(`${ouvrage.numero}|${String(ressource.rang)}`) ?? null
                      }
                      modifiable={modifiable}
                      enAttente={enAttente === cle}
                      onFixer={(provenance) => {
                        onModifier({
                          poste: ouvrage.numero,
                          rang: ressource.rang,
                          provenance,
                        });
                      }}
                    />
                    {/* Le geste du 08/09/2026 : sur un ARTICLE coché « du groupe », la
                        provenance est le magasin — et c'est ici qu'on va voir ce qu'il en
                        reste, puis qu'on le réserve. */}
                    {surStock && (
                      <button
                        type="button"
                        /* Sur sa propre ligne : la cellule de provenance porte déjà six champs
                           et, quand aucun site n'est au référentiel, une note en toutes lettres
                           — le lien s'y collait, et on lisait « aucun site au
                           référentielstock du magasin ». */
                        className="lien lien-stock"
                        title="Voir le stock du magasin et réserver pour ce chantier"
                        onClick={() => {
                          setStockOuvert(stockOuvert === cle ? null : cle);
                        }}
                      >
                        {stockOuvert === cle ? "masquer le stock" : "stock du magasin"}
                      </button>
                    )}
                  </td>
                  <td>{ressource.unite}</td>
                  <td className="col-nombre col-devis">
                    {origine === undefined ? "—" : formaterNombre(origine.quantiteReelle, 4)}
                  </td>
                  <td className="col-nombre col-devis">
                    {origine === undefined ? "—" : formaterNombre(origine.prixUnitaire, 4)}
                  </td>
                  <td className={colonneSaisie} title={`Temps unitaire : ${ressource.tempsUnitaire}`}>
                    <Saisie
                      valeur={ressource.quantiteReelle}
                      decimales={4}
                      modifiable={modifiable}
                      modifiee={quantiteModifiee}
                      enAttente={enAttente === cle}
                      onValider={(valeur) => {
                        onModifier({
                          poste: ouvrage.numero,
                          rang: ressource.rang,
                          quantite: valeur,
                        });
                      }}
                    />
                  </td>
                  <td className={colonneSaisie}>
                    <Saisie
                      valeur={ressource.prixUnitaire}
                      decimales={4}
                      modifiable={modifiable}
                      modifiee={prixModifie}
                      enAttente={enAttente === cle}
                      onValider={(valeur) => {
                        onModifier({
                          poste: ouvrage.numero,
                          rang: ressource.rang,
                          prixUnitaire: valeur,
                        });
                      }}
                    />
                  </td>
                  <td className="col-nombre">{formaterMontant(ressource.cout)}</td>
                  {/* La colonne « Vente » de l'arbre : une ressource n'a pas de prix de
                      vente. Vide, et non supprimée — c'est ce qui garde le Δ du détail
                      sous le Δ de l'arbre. */}
                  <td />
                  <td className="col-nombre">
                    <Delta valeur={delta} />
                  </td>
                  <td />
                  <td className="statut">
                    {/* Le statut vient du serveur : c'est lui qui le déduit, et
                        le recalculer ici donnerait un jour deux réponses. */}
                    {ressource.statut === "conserve"
                      ? ""
                      : (STATUTS_LIGNE[ressource.statut as keyof typeof STATUTS_LIGNE] ??
                        ressource.statut)}
                  </td>
                  <td className="gestes">
                    {modifiable && (
                      <button
                        type="button"
                        className="lien"
                        disabled={enAttente === cle}
                        onClick={() => {
                          onModifier({
                            poste: ouvrage.numero,
                            rang: ressource.rang,
                            supprimee: !retiree,
                          });
                        }}
                      >
                        {retiree ? "Remettre" : "Retirer"}
                      </button>
                    )}
                  </td>
                </tr>
                {/* Le panneau sur toute la largeur, SOUS la ligne qui l'a ouvert.

                    Dans la cellule de provenance il aurait fait 12 % de la table ; en dialogue
                    flottant il aurait masqué la ligne dont il parle. Ici, il reste sous les yeux
                    à côté de la quantité qu'on vient réserver. */}
                {surStock && stockOuvert === cle && (
                  <tr className="ligne-stock">
                    <td colSpan={COLONNES}>
                      <StockDeLaLigne
                        chantierId={stock.chantierId}
                        code={ressource.code}
                        quantiteDeLaLigne={ressource.quantiteReelle}
                        modifiable={modifiable}
                        onFermer={() => {
                          setStockOuvert(null);
                        }}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {ouvrage.ressources.length === 0 && (
              <tr>
                <td colSpan={COLONNES} className="vide">
                  Cet ouvrage ne porte aucune ligne de ressource.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {modifiable && (
          <AjoutRessource
            generiques={generiques}
            articlesTarif={articlesTarif}
            onAjouter={(code, quantite) => {
              onModifier({ poste: ouvrage.numero, rang: 0, ajouterCode: code, quantite });
            }}
          />
        )}
      </td>
    </tr>
  );
}

/**
 * D'où vient ce matériau : la case du groupe, et la carrière.
 *
 * *Décision du métier du 12/08/2026 : « une case à cocher pour le groupe, et à
 * partir du moment où c'est coché indiquer la carrière de provenance. C'est le
 * conducteur qui décide si cela vient du groupe et de quelle carrière. »* D'où une
 * saisie sur la LIGNE, pendant la contre-étude, et non un attribut de référentiel :
 * le même 0/20 vient de la carrière la plus proche du chantier.
 *
 * Trois états, et le troisième est celui qui compte :
 *
 *  - **décoché** — acheté à l'extérieur, rien à dire de plus ;
 *  - **coché avec sa carrière** — la ligne est complète, elle alimentera le planning
 *    de livraison et celui de production des carrières ;
 *  - **coché sans carrière** — « c'est de chez nous, je ne sais pas encore d'où ».
 *    C'est un état LÉGITIME d'une contre-étude en cours, et l'écran doit le montrer
 *    pour le réclamer plutôt que de le laisser passer inaperçu.
 *
 * **Depuis le 18/08/2026, désigner la carrière POSE LE PRIX de la grille tarifaire.** La
 * ligne se marque donc comme retravaillée, et l'écart au devis figé apparaît — c'est le but,
 * puisque c'est là que se lit le gain de s'approvisionner dans le groupe. Décocher rend le
 * prix du devis.
 *
 * D'où le quatrième état, celui que cette colonne doit surtout ne pas taire : **coché, avec sa
 * carrière, et aucun prix dans la grille — la ligne est alors chiffrée à ZÉRO** *(règle du
 * métier, 18/08/2026)*. 37 articles pour six carrières font 222 croisements dont 70 seulement
 * portent un prix, et des lignes de déboursé ne sont pas des matériaux de carrière du tout :
 * c'est donc le cas courant, et le seul endroit qui explique pourquoi un poste ne coûte plus
 * rien.
 */
function Provenance({
  ressource,
  carrieres,
  motifSansTarif,
  modifiable,
  enAttente,
  onFixer,
}: {
  ressource: RessourceVue;
  carrieres: readonly Carriere[];
  /** Pourquoi la grille n'a pas donné de prix, ou `null` si elle en a donné un. */
  motifSansTarif: MotifSansTarif | null;
  modifiable: boolean;
  enAttente: boolean;
  onFixer: (provenance: ChangementAcheminement) => void;
}): JSX.Element {
  // Une ligne en heures ordinaire ne se livre pas et n'apporte rien. La colonne reste
  // vide plutôt que de porter une case grisée : 69 lignes sur 79 d'un chantier réel
  // sont dans ce cas, et autant de cases inertes noieraient les dix qui appellent une
  // réponse.
  if (!ressource.provenanceApplicable) return <span className="neutre" />;

  const proposables = carrieres.filter((c) => c.actif || c.id === ressource.carriereId);

  /*
    **Les SIX champs, et non deux** (migration 041).

    *« Sur la contre-étude sur les matériaux je vois bien la coche groupe avec la sélection du
    site, il faudrait avoir déjà en plus le mode de livraison à sélectionner […] pour les
    articles commençant par 100 il faut faire la même chose dès la contre-étude. »* (métier,
    21/08/2026).

    C'est **le même composant** que sur les deux pastilles du planning : trois panneaux
    jumeaux finiraient par ne plus proposer les mêmes modes, et c'est exactement la panne
    qu'on vient de réparer — l'acheminement n'avait été posé que sur la pastille du camion.

    Ce qui est saisi ici est une **valeur de départ** : elle descend sur la pastille à sa
    création, et n'y touche plus jamais ensuite. Choisir un mode fait naître la pastille —
    l'écran l'annonce, sans quoi une livraison apparue sans clic passerait pour un défaut.
  */
  return (
    <>
      <Acheminement
        porteur={ressource}
        carrieres={carrieres}
        modifiable={modifiable}
        enAttente={enAttente}
        onModifier={(changement) => {
          // `duGroupe` voyage TOUJOURS : c'est la question que la ligne pose depuis la 010, et
          // le serveur l'exige. Sans elle, corriger un fournisseur enverrait une demande sans
          // réponse à « ce matériau vient-il du groupe ? » — donc un refus, alors que la
          // réponse est sous les yeux. Le changement est écrit après, il l'emporte.
          onFixer({ duGroupe: ressource.duGroupe, ...changement });
        }}
      />
      {/* Aucun site au référentiel : le menu serait vide et sans explication. */}
      {ressource.duGroupe && proposables.length === 0 && (
        <span className="note"> aucun site au référentiel</span>
      )}
      {motifSansTarif !== null && (
        <span className="sans-tarif" title={EXPLICATIONS_SANS_TARIF[motifSansTarif]}>
          {" "}
          {LIBELLES_SANS_TARIF[motifSansTarif]}
        </span>
      )}
    </>
  );
}

/**
 * Court, parce que la colonne est étroite ; l'infobulle dit le reste.
 *
 * Les trois libellés disent **« à 0 »** parce que c'est ce qui vient d'arriver à la ligne
 * *(règle du métier, 18/08/2026)*. Nommer seulement la cause — « prix manquant » — laisserait
 * croire que le chiffrage précédent tient encore.
 */
const LIBELLES_SANS_TARIF: Record<MotifSansTarif, string> = {
  article_hors_grille: "à 0 · hors grille",
  hors_periode: "à 0 · hors période",
  prix_absent: "à 0 · prix manquant",
};

const EXPLICATIONS_SANS_TARIF: Record<MotifSansTarif, string> = {
  article_hors_grille:
    "Ce code n'est pas dans la grille tarifaire : ce n'est pas un matériau de carrière, ou il " +
    "reste à y ajouter. La ligne est chiffrée à 0 — ajouter l'article et son prix au " +
    "référentiel, puis redésigner la carrière. Décocher « groupe » rend le prix du devis.",
  hors_periode:
    "Aucune période de validité de la grille ne couvre la date de démarrage de ce chantier : " +
    "la ligne est chiffrée à 0. Créer la période au référentiel, puis redésigner la carrière.",
  prix_absent:
    "La grille ne porte aucun prix pour cet article dans cette carrière : la ligne est " +
    "chiffrée à 0. Renseigner le prix au référentiel, puis redésigner la carrière.",
};

/**
 * Ce que les pastilles d'une ligne de transport totalisent, et le dépassement.
 *
 * *« Tu peux annoncer colonne H posée le dépassement »* (métier, 13/08/2026, revenant sur
 * sa consigne initiale de n'y rien calculer — parce que les pastilles font désormais bouger
 * les heures et le déboursé).
 *
 * Trois états, et le troisième est celui qui appelle une décision :
 *
 *  - **rien de posé** — la ligne est chiffrée mais personne n'a encore dit quand le camion
 *    roule. L'écran le dit plutôt que d'afficher un zéro, qui se lirait comme « aucune
 *    heure » ;
 *  - **posé = chiffré** — la ligne est soldée, et on l'annonce d'un simple total ;
 *  - **dépassement** — plus (ou moins) de transport sur le calendrier que le déboursé n'en
 *    porte. Il ne peut venir que d'un nombre d'unités déclaré après coup sur une ligne à
 *    plusieurs pastilles : toute écriture de pastille recale la ligne. **Il s'affiche, il
 *    ne bloque pas**, comme le reste à livrer et le coût de l'arrondi.
 */
function HeuresPosees({ ressource }: { ressource: RessourceVue }): JSX.Element | null {
  if (!ressource.estTransport) return null;

  const { transports, heuresPosees, depassementTransport } = ressource.planning;
  if (transports.length === 0) {
    return <span className="note note-ligne">aucun transport posé</span>;
  }

  const depassement = Number(depassementTransport);
  // Le seuil vient du MOTEUR, et c'est le même que celui du recalage côté serveur : sous
  // cette valeur, l'écart est un résidu d'arrondi de la pastille — pas du travail. Deux
  // seuils différents afficheraient un dépassement que rien ne corrigerait.
  const significatif = Math.abs(depassement) >= Number(TOLERANCE_HEURE);

  return (
    <span className={`note-ligne ${significatif ? "alerte-douce" : "note"}`}>
      {formaterNombre(heuresPosees, 2)} h posées
      {significatif && (
        <>
          {" "}
          <strong>
            {depassement > 0 ? "+" : "−"}
            {formaterNombre(String(Math.abs(depassement)), 2)} h
          </strong>
        </>
      )}
    </span>
  );
}

/**
 * Le choix de ce qui prend la place d'une ligne : une ressource, ou un matériau.
 *
 * Une liste déroulante ordinaire, pas un champ de recherche : à 139 entrées
 * groupées par famille, le navigateur fait déjà le travail — on tape les
 * premières lettres, il y va. Un composant de recherche maison serait du code à
 * maintenir pour reproduire ce qui existe.
 *
 * **Les matériaux de la grille tarifaire y sont depuis le 27/08/2026** — voir
 * `remplacement.ts`, qui compose les groupes et dit pourquoi le catalogue d'achat n'y est pas.
 *
 * La ressource courante est ajoutée à la liste si elle n'y figure pas : un code
 * retiré du référentiel, ou venu d'un déboursé plus ancien, ne doit pas
 * disparaître du menu — la ligne semblerait alors pointer sur autre chose.
 */
function ChoixRessource({
  valeur,
  generiques,
  articlesTarif,
  enAttente,
  onChoisir,
}: {
  valeur: string;
  generiques: readonly Generique[];
  articlesTarif: readonly ArticleTarif[];
  enAttente: boolean;
  onChoisir: (code: string) => void;
}): JSX.Element {
  const groupes = groupesDeRemplacement(generiques, articlesTarif);
  const connu = estProposee(valeur, groupes);

  return (
    <select
      className="choix-ressource"
      value={valeur}
      disabled={enAttente}
      onChange={(e) => {
        if (e.target.value !== valeur) onChoisir(e.target.value);
      }}
      aria-label="Remplacer cette ressource"
    >
      {!connu && <option value={valeur}>{valeur} (hors référentiel)</option>}
      {groupes.map((groupe) => (
        <optgroup key={groupe.titre} label={groupe.titre}>
          {groupe.options.map((option) => (
            <option key={option.cle} value={option.code}>
              {option.code} — {option.libelle}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** Ajouter une ressource à un ouvrage : le code, la quantité, et c'est tout. */
/**
 * Ajouter une ressource générique, **ou un article**.
 *
 * *« pouvoir ajouter les ressources ou article […] le comportement des articles et ressources
 * doit être le même que les autres existants »* (Chris, 21/08/2026).*
 *
 * Deux champs pour **trois** référentiels, et c'est la taille qui décide, pas la nature : 102
 * ressources génériques et 37 matériaux de la grille tarifaire tiennent dans une liste
 * déroulante, les ~3 000 articles du catalogue d'achat non — une liste de 3 000 options se
 * charge mal et ne se lit pas. L'article du catalogue se cherche donc par frappe, sur son code
 * ou son libellé, et la recherche vit côté serveur : charger le référentiel entier pour filtrer
 * dans le navigateur ferait payer trente colonnes par fiche à chaque ouverture d'un ouvrage.
 *
 * **Un matériau de la grille entre à 0 €** : son prix vit au croisement article × carrière ×
 * période, et c'est cocher « du groupe » puis désigner la carrière qui le pose (règle du
 * 18/08/2026). La liste déroulante ne peut donc pas l'annoncer — il n'y a pas un prix, il y en
 * a un par carrière.
 *
 * Choisir dans l'un vide l'autre : la ligne ajoutée est une ressource **ou** un article, et
 * deux champs remplis laisseraient le bouton décider — c'est-à-dire deviner.
 *
 * Le coût d'étude est affiché dans les propositions. Il sera posé sur la ligne, et un article
 * dont l'ERP ne le renseigne pas donnera une ligne à zéro : autant que ça se voie avant de
 * l'ajouter.
 */
function AjoutRessource({
  generiques,
  articlesTarif,
  onAjouter,
}: {
  generiques: readonly Generique[];
  articlesTarif: readonly ArticleTarif[];
  onAjouter: (code: string, quantite: string) => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [quantite, setQuantite] = useState("");
  const [cherche, setCherche] = useState("");
  const [articles, setArticles] = useState<readonly ArticleTrouve[]>([]);
  // Vrai quand d autres articles correspondaient sans etre rendus : a dire, jamais a taire.
  const [tronque, setTronque] = useState(false);
  const [total, setTotal] = useState(0);
  const [codeArticle, setCodeArticle] = useState("");

  /**
   * La recherche part après un temps d'arrêt, et le résultat en retard est jeté.
   *
   * Sans le second point, taper vite fait arriver les réponses dans un ordre quelconque et la
   * liste affiche celle d'une frappe précédente — une liste qui ne correspond pas à ce qui est
   * écrit dans le champ. Le drapeau du nettoyage d'effet est ce qui l'empêche.
   *
   * Une panne de la recherche laisse la liste vide et n'efface rien d'autre : le code peut
   * toujours être tapé en entier, et le serveur dira s'il existe.
   */
  useEffect(() => {
    // Le code NU, et non le texte de la proposition choisie : sinon choisir dans la liste
    // relance une recherche sur « code — libellé », qui ne trouve rien et vide la liste.
    const terme = codeDuChampArticle(cherche);
    if (terme.length < 2) {
      setArticles([]);
      setTronque(false);
      setTotal(0);
      return;
    }
    let abandonne = false;
    const minuteur = setTimeout(() => {
      chercherArticles(terme)
        .then((reponse) => {
          if (abandonne) return;
          setArticles(reponse.articles);
          setTronque(reponse.tronque);
          setTotal(reponse.total);
        })
        .catch(() => {
          if (!abandonne) {
            setArticles([]);
            setTronque(false);
          }
        });
    }, 250);
    return () => {
      abandonne = true;
      clearTimeout(minuteur);
    };
  }, [cherche]);

  /**
   * Le code retenu : celui de la liste déroulante, ou celui de l'article cherché.
   *
   * Le code de l'article se lit **dans le texte du champ** et non dans un état à part : la
   * frappe libre doit rester possible — un code tapé en entier s'ajoute sans passer par la
   * liste, et le serveur dira s'il existe. Voir `codeArticle.ts`, qui retire le libellé quand la
   * saisie porte la forme `« code — libellé »`.
   */
  const retenu = code !== "" ? code : codeDuChampArticle(codeArticle);

  /** Choisir un article de la liste : le champ porte le code ET le libellé, pour qu'on relise. */
  const choisirLArticle = (article: ArticleTrouve): void => {
    setCodeArticle(`${article.code} — ${article.description}`);
    // La recherche s'arrête là : la liste se referme, et le terme n'est pas relancé sur un texte
    // qui ne ressemble ni à un code ni à un libellé.
    setCherche("");
    setArticles([]);
    setTronque(false);
    setTotal(0);
    setCode("");
  };

  return (
    <form
      className="ajout ajout--ligne"
      onSubmit={(e) => {
        e.preventDefault();
        if (retenu === "" || quantite.trim() === "") return;
        onAjouter(retenu, quantite.trim());
        setCode("");
        setQuantite("");
        setCodeArticle("");
        setCherche("");
      }}
    >
      <select
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
          // Choisir une ressource abandonne l'article en cours de frappe : la ligne est
          // l'une ou l'autre, et laisser les deux remplis ferait deviner le bouton.
          if (e.target.value !== "") {
            setCodeArticle("");
            setCherche("");
          }
        }}
        aria-label="Ressource à ajouter"
      >
        <option value="">— ajouter une ressource —</option>
        <optgroup label="Main-d'œuvre">
          {generiques
            .filter((g) => g.type === "MO")
            .map((g) => (
              <option key={g.id} value={g.code}>
                {g.code} — {g.libelle}
              </option>
            ))}
        </optgroup>
        <optgroup label="Matériel">
          {generiques
            .filter((g) => g.type === "MAT")
            .map((g) => (
              <option key={g.id} value={g.code}>
                {g.code} — {g.libelle}
              </option>
            ))}
        </optgroup>
        {/*
          **Après « Matériel »** *(demande de Chris, 26/08/2026)*. Les 37 matériaux de la grille
          tarifaire tiennent dans une liste déroulante, contrairement aux ~3 000 articles du
          catalogue d'achat — c'est pour ça qu'ils sont ici et non dans le champ de recherche.

          Le groupe n'apparaît pas quand la liste est vide : une installation sans grille, ou
          une lecture qui n'a pas abouti, donnent le même écran qu'avant. C'est un ensemble de
          choix et non un écran d'alerte — il n'y a rien à y compter.
        */}
        {articlesTarif.length > 0 && (
          <optgroup label="Grille tarifaire des carrières">
            {articlesTarif.map((a) => (
              <option key={a.id} value={a.code}>
                {a.code} — {a.libelle}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {/*
        LA RECHERCHE D'ARTICLE, avec sa PROPRE liste de résultats.

        *Demande de Chris, 08/09/2026 : « lorsqu'on tape le libellé de l'article il faudrait que
        cela recherche automatiquement dans la table article sur description ou code article […]
        il faut que cela recherche dans tout le texte ou code : je tape "oude", tous les coudes
        apparaissent et je sélectionne ; ou si je tape "10021", tous les 10021 apparaissent. »*

        C'était un `<datalist>` jusqu'ici, et c'est précisément ce qui ne pouvait pas tenir cette
        promesse : le serveur cherche bien `code ILIKE '%…%' OR description ILIKE '%…%'`, mais
        **le navigateur refiltre les propositions d'un datalist selon sa propre règle** — souvent
        par le début du texte, et différemment d'une version à l'autre. Ce qui remontait du
        serveur n'arrivait donc pas forcément à l'écran, et personne ne pouvait le prévoir.

        Une liste que nous dessinons ne filtre rien : elle affiche ce que le serveur a trouvé, et
        rien d'autre. Même parti pris que la recherche de l'écran Stocks, qui n'a jamais eu ce
        défaut pour cette raison.
      */}
      <div className="ajout__article">
        <input
          className="saisie--article"
          placeholder="ou un code / libellé d'article"
          value={codeArticle}
          onChange={(e) => {
            setCodeArticle(e.target.value);
            setCherche(e.target.value);
            if (e.target.value !== "") setCode("");
          }}
          aria-label="Article à ajouter"
        />
        {articles.length > 0 && (
          <ul className="ajout__trouves">
            {articles.map((a) => (
              <li key={a.code}>
                <button
                  type="button"
                  onClick={() => {
                    choisirLArticle(a);
                  }}
                >
                  <strong>{a.code}</strong>
                  <span>{a.description}</span>
                  {/* L'unité et le coût d'étude : c'est le prix que la ligne prendra, et le
                      seul moyen de choisir entre deux articles de libellé voisin. */}
                  {/* Deux décimales, et non les cinq que la colonne porte : c'est un repère
                      pour choisir entre deux articles voisins, pas le prix qui sera posé — le
                      serveur reprend le coût d'étude exact à l'ajout. */}
                  <span className="ajout__trouves-prix">
                    {a.unite ?? ""}{" "}
                    {a.coutEtude === null
                      ? "sans coût d'étude"
                      : `${formaterNombre(a.coutEtude, 2)} €`}
                  </span>
                </button>
              </li>
            ))}
            {/* La liste est coupée au plafond : le dire, sinon on cherche une ligne qui
                correspond, qui existe, et qui n'a simplement pas été rendue. */}
            {tronque && (
              <li className="ajout__trouves-tronque">
                {total} articles correspondent, {articles.length} affichés — ajoutez un mot pour
                préciser (« oude 110 »).
              </li>
            )}
          </ul>
        )}
      </div>
      <input
        placeholder="Heures / quantité"
        value={quantite}
        onChange={(e) => {
          setQuantite(e.target.value);
        }}
        aria-label="Quantité totale"
      />
      <button type="submit" disabled={retenu === "" || quantite.trim() === ""}>
        Ajouter
      </button>
    </form>
  );
}

/**
 * Le glisser-déposer d'un bloc, rassemblé en un seul objet (23/09/2026).
 *
 * Six valeurs passées une à une auraient ajouté six lignes à chacun des deux points de
 * rendu de `LigneNoeud` — et à chaque prochain. Un objet se transmet en un mot, et c'est
 * ce qui évite qu'un des deux chemins de rendu en oublie une : le rendu récursif et le
 * rendu des racines doivent toujours recevoir la même chose.
 */
type ContexteDeplacement = {
  /** Faux sur un devis figé, une CE de suivi, ou une version qu'on ne peut pas modifier. */
  readonly actif: boolean;
  readonly glisse: string | null;
  readonly survol: { readonly numero: string; readonly moitie: MoitieSurvolee } | null;
  readonly demarrer: (numero: string) => void;
  /** La position du curseur accompagne le survol : c'est elle qui pilote le défilement. */
  readonly survoler: (
    numero: string,
    moitie: MoitieSurvolee,
    position: { x: number; y: number },
  ) => void;
  readonly deposer: (numero: string, moitie: MoitieSurvolee) => void;
  readonly terminer: () => void;
};

/** La moitié de ligne survolée, d'après la position du curseur dans sa hauteur. */
function moitieSurvolee(evenement: { clientY: number }, ligne: HTMLElement): MoitieSurvolee {
  const cadre = ligne.getBoundingClientRect();
  return evenement.clientY < cadre.top + cadre.height / 2 ? "haute" : "basse";
}

function LigneNoeud({
  noeud,
  reference,
  ordonnancement,
  profondeur,
  ouverts,
  basculer,
  modifiable,
  enAttente,
  generiques,
  articlesTarif,
  groupes,
  carrieres,
  sansTarif,
  stock,
  aMontrer,
  deplacement,
  onModifier,
}: {
  noeud: NoeudVue;
  reference: Map<string, NoeudVue> | null;
  /** La variation d'ordonnancement de chaque chapitre. Voir `variationsDOrdonnancement`. */
  ordonnancement: Ordonnancement | null;
  profondeur: number;
  ouverts: ReadonlySet<string>;
  basculer: (numero: string) => void;
  modifiable: boolean;
  enAttente: string | null;
  generiques: readonly Generique[];
  articlesTarif: readonly ArticleTarif[];
  groupes: readonly GroupeAdmin[];
  carrieres: readonly Carriere[];
  sansTarif: ReadonlyMap<string, MotifSansTarif>;
  /** Où le panneau de stock s'ouvre, et pour quel chantier (070). */
  stock: ContexteStock;
  /** Restreindre l'affichage à ces numéros de poste. `null` = tout montrer. */
  aMontrer: ReadonlySet<string> | null;
  deplacement: ContexteDeplacement;
  onModifier: (modification: Modification) => void;
}): JSX.Element | null {
  const ouvert = ouverts.has(noeud.numero);
  const pliable = noeud.nature === "chapitre" ? noeud.enfants.length > 0 : true;
  const origine = reference?.get(noeud.numero) ?? null;
  const deltaTotal = ecartAuDevis(noeud.montantRecalcule, origine?.montantRecalcule);
  /*
    Le Δ ne porte plus que le CHIFFRAGE (24/09/2026) : ce qu'un chapitre a reçu ou cédé par
    déplacement de blocs se lit dans sa propre colonne. Les deux se somment au Δ d'avant.
  */
  const ordonnancementDuNoeud = ordonnancement?.affichee.get(noeud.numero) ?? null;
  // La part retirée du Δ est la STRUCTURE, et non la colonne affichée : au premier niveau,
  // celle-ci compte en plus ce qu'un bloc remonté y apporte (voir `variationsDOrdonnancement`).
  const structureDuNoeud = ordonnancement?.structure.get(noeud.numero) ?? null;
  const deltaChiffrage =
    deltaTotal !== null && structureDuNoeud !== null ? deltaTotal - structureDuNoeud : deltaTotal;
  // Le filtre s applique ICI plutot qu a la construction de la liste : l arbre
  // garde sa forme, et un chapitre dont rien n est a montrer disparait avec sa
  // branche sans qu on ait a la reconstruire.
  if (aMontrer !== null && !aMontrer.has(noeud.numero)) return null;

  const clePoste = `${noeud.numero}#0`;

  return (
    <>
      <tr
        /*
          LE GLISSER-DÉPOSER D'UN BLOC (23/09/2026).

          Posé sur la ligne du poste, donc le bloc entier suit : le rattachement porte tout
          ce qui pend dessous, et rien d'autre n'a à être déplacé.

          `onDragOver` doit appeler `preventDefault` — sans lui le navigateur refuse le
          dépôt, et la ligne reste inerte sans qu'aucune erreur ne le dise.
        */
        draggable={deplacement.actif}
        /* Le numéro sur la ligne : c'est par lui que la boucle de défilement retrouve la
           cible réellement sous le curseur, quand la page a bougé sans qu'aucun événement
           de survol ne se soit déclenché. */
        data-poste={noeud.numero}
        onDragStart={() => deplacement.demarrer(noeud.numero)}
        onDragEnd={deplacement.terminer}
        onDragOver={(evenement) => {
          if (!deplacement.actif || deplacement.glisse === null) return;
          evenement.preventDefault();
          deplacement.survoler(noeud.numero, moitieSurvolee(evenement, evenement.currentTarget), {
            x: evenement.clientX,
            y: evenement.clientY,
          });
        }}
        onDrop={(evenement) => {
          if (!deplacement.actif || deplacement.glisse === null) return;
          evenement.preventDefault();
          deplacement.deposer(noeud.numero, moitieSurvolee(evenement, evenement.currentTarget));
        }}
        className={
          (noeud.nature === "chapitre"
            ? `chapitre niveau-${Math.min(profondeur, 3)}`
            : "ouvrage") +
          (noeud.retenu ? "" : " ecarte") +
          (deplacement.glisse === noeud.numero ? " en-deplacement" : "") +
          (deplacement.survol?.numero === noeud.numero
            ? ` depot-${deplacement.survol.moitie}`
            : "")
        }
      >
        <td className="col-retenu">
          {/* La case n'apparaît QUE sur les options et les variantes, pas sur
              les 131 postes du déboursé : demande de Chris, et un tableau de
              chiffres criblé de cases cesse de se lire comme un tableau. Les
              lignes de ressources gardent leur bouton « Retirer ». */}
          {noeud.ecartable && (
            <input
              type="checkbox"
              checked={noeud.retenu}
              disabled={!modifiable || enAttente === clePoste}
              title={
                noeud.retenu
                  ? "Compté dans le déboursé et dans le chiffre d'affaires. Décocher pour l'écarter."
                  : "Écarté : ne compte ni en charges, ni en heures, ni en chiffre d'affaires."
              }
              aria-label={`Compter le poste ${noeud.numero} dans le chiffrage`}
              onChange={(e) => {
                onModifier({ poste: noeud.numero, rang: 0, retenu: e.target.checked });
              }}
            />
          )}
        </td>
        <td className="col-ligne">{noeud.ligneTableur}</td>
        <td className="col-numero">
          {pliable ? (
            <button
              type="button"
              className="plier"
              onClick={() => {
                basculer(noeud.numero);
              }}
              aria-label={`${ouvert ? "Replier" : "Déplier"} le poste ${noeud.numero}`}
              aria-expanded={ouvert}
            >
              {ouvert ? "▾" : "▸"}
            </button>
          ) : (
            <span className="plier plier--vide" />
          )}
          <span style={{ paddingLeft: `${profondeur * 0.9}rem` }}>{noeud.numero}</span>
        </td>
        <td className="designation">
          <Designation
            noeud={noeud}
            modifiable={modifiable}
            enAttente={enAttente === clePoste}
            onModifier={onModifier}
          />
        </td>
        <td className="col-groupe">
          <ChoixGroupe
            noeud={noeud}
            groupes={groupes}
            modifiable={modifiable}
            enAttente={enAttente === clePoste}
            onAffecter={(groupeId) => {
              onModifier({ poste: noeud.numero, rang: 0, groupeId });
            }}
          />
        </td>
        <td>{noeud.nature === "ouvrage" ? noeud.unite : ""}</td>
        <td className="col-nombre heures">
          {/* Zéro heure sur un poste ne s'affiche pas : sur les déboursés réels,
              beaucoup d'ouvrages ne portent que des fournitures, et une colonne
              de « 0,00 » ferait chercher une erreur là où il n'y en a pas. */}
          {noeud.heures === "0" ? "" : `${formaterNombre(noeud.heures, 2)} h`}
        </td>
        <td className="col-nombre col-devis">
          {origine === null ? "" : formaterMontant(origine.montantRecalcule)}
        </td>
        {/* La quantité de vente, dans la colonne de la quantité du détail — voir
            l'en-tête. Elle vient du classeur et ne se saisit pas. */}
        <td className="col-nombre">
          {noeud.nature === "ouvrage" ? formaterNombre(noeud.quantiteVente, 3) : ""}
        </td>
        <td className="col-nombre col-devis vente">
          {origine === null ? "" : formaterMontant(origine.prixVente)}
        </td>
        <td className="col-nombre montant">{formaterMontant(noeud.montantRecalcule)}</td>
        {/* Le prix de vente en face du déboursé, à chaque niveau de l'arbre :
            un coût sans son chiffre d'affaires ne dit pas si le poste gagne ou
            perd de l'argent. Sur un poste écarté, les deux valent zéro pour le
            chantier — ce qui reste affiché est ce que le poste PÈSERAIT. */}
        <td className="col-nombre vente">{formaterMontant(noeud.prixVente)}</td>
        <td className="col-nombre">
          <Delta valeur={deltaChiffrage} />
        </td>
        <td className="col-nombre">
          {ordonnancementDuNoeud === null ? "" : <Delta valeur={ordonnancementDuNoeud} neutre />}
        </td>
        <td className="col-nombre annonce">{formaterMontant(noeud.montantAnnonce)}</td>
        <td className={`col-nombre ${classeEcartFichier(noeud)}`}>
          {formaterMontant(noeud.ecart)}
        </td>
      </tr>

      <Anomalie noeud={noeud} />

      {ouvert && noeud.nature === "ouvrage" && (
        <Ressources
          ouvrage={noeud}
          reference={origine?.nature === "ouvrage" ? origine : null}
          modifiable={modifiable}
          enAttente={enAttente}
          generiques={generiques}
          articlesTarif={articlesTarif}
          carrieres={carrieres}
          sansTarif={sansTarif}
          stock={stock}
          onModifier={onModifier}
        />
      )}
      {ouvert &&
        noeud.nature === "chapitre" &&
        noeud.enfants.map((enfant) => (
          <LigneNoeud
            key={enfant.numero}
            noeud={enfant}
            reference={reference}
            ordonnancement={ordonnancement}
            profondeur={profondeur + 1}
            ouverts={ouverts}
            basculer={basculer}
            modifiable={modifiable}
            enAttente={enAttente}
            generiques={generiques}
          articlesTarif={articlesTarif}
          groupes={groupes}
          carrieres={carrieres}
          sansTarif={sansTarif}
          stock={stock}
          aMontrer={aMontrer}
          deplacement={deplacement}
            onModifier={onModifier}
          />
        ))}
    </>
  );
}

/* ---------------------------------------------------------------------------
 * L'écran
 * ------------------------------------------------------------------------- */

function tousLesNumeros(noeuds: readonly NoeudVue[]): string[] {
  return noeuds.flatMap((noeud) =>
    noeud.nature === "chapitre"
      ? [noeud.numero, ...tousLesNumeros(noeud.enfants)]
      : [noeud.numero],
  );
}

/**
 * Les tâches sans famille de travaux, **et les chapitres qui y mènent**.
 *
 * Les parents sont inclus à dessein : un filtre qui ne garderait que les tâches les
 * ferait disparaître avec leur branche, puisque masquer un chapitre masque ses
 * enfants. Le filtre afficherait alors « rien » là où il y a du travail — le défaut
 * exact qu'il est censé éviter.
 *
 * Les postes écartés du chiffrage sont ignorés : ils ne comptent pas dans le
 * contrôle du §4.3, donc ils n'ont pas à apparaître dans la liste de ce qui reste.
 */
function numerosSansFamille(noeuds: readonly NoeudVue[]): string[] {
  const trouves: string[] = [];

  const descendre = (liste: readonly NoeudVue[]): boolean => {
    let quelqueChose = false;
    for (const noeud of liste) {
      if (!noeud.retenu) continue;
      if (noeud.nature === "ouvrage") {
        if (noeud.groupe === null) {
          trouves.push(noeud.numero);
          quelqueChose = true;
        }
        continue;
      }
      // Le chapitre n'est retenu dans le filtre que si sa descendance porte
      // quelque chose : sinon on afficherait des branches vides.
      if (descendre(noeud.enfants)) {
        trouves.push(noeud.numero);
        quelqueChose = true;
      }
    }
    return quelqueChose;
  };

  descendre(noeuds);
  return trouves;
}

function numerosChapitres(noeuds: readonly NoeudVue[]): string[] {
  return noeuds.flatMap((noeud) =>
    noeud.nature === "chapitre" ? [noeud.numero, ...numerosChapitres(noeud.enfants)] : [],
  );
}

export function Debourse({
  entete,
  depo,
  reference,
  libelleReference,
  modifiable,
  enAttente,
  generiques,
  groupes,
  carrieres,
  stock,
  onDeplacer,
  onModifier,
}: {
  entete: EnteteVersion;
  depo: DepoVue;
  reference: DepoVue | null;
  libelleReference: string | null;
  modifiable: boolean;
  enAttente: string | null;
  generiques: readonly Generique[];
  groupes: readonly GroupeAdmin[];
  carrieres: readonly Carriere[];
  /** Où le panneau de stock s'ouvre, et pour quel chantier (070). */
  stock: ContexteStock;
  /**
   * Déplacer un bloc, quand l'écran qui accueille sait le faire (23/09/2026).
   *
   * Optionnelle : absente, le glisser-déposer ne s'arme pas — plutôt qu'il s'arme et que
   * le bloc retombe à sa place sans que rien ne l'explique.
   */
  onDeplacer?: (bloc: string, pose: Pose) => void;
  onModifier: (modification: Modification) => void;
}): JSX.Element {
  // Les chapitres sont ouverts d'emblée, les ouvrages fermés : l'arbre du
  // déboursé doit se voir en entier, mais ses centaines de lignes de ressources
  // noieraient la structure qu'on vient chercher.
  const [ouverts, setOuverts] = useState<ReadonlySet<string>>(
    () => new Set(numerosChapitres(depo.racines)),
  );

  const basculer = (numero: string): void => {
    setOuverts((precedents) => {
      const suivants = new Set(precedents);
      if (!suivants.delete(numero)) suivants.add(numero);
      return suivants;
    });
  };

  /*
    LE GLISSER-DÉPOSER D'UN BLOC (23/09/2026).

    *« Je veux prendre les blocs par drag and drop pour aller vite être précis. »* (Chris)

    Il ne s'arme que sur une COPIE DE TRAVAIL modifiable — *« je ne demanderai pas la même
    chose à partir d'une CE de suivi modifiable »*. Le serveur refuse de toute façon les
    autres types : ici on évite seulement de proposer un geste qui serait rejeté.
  */
  const [glisse, setGlisse] = useState<string | null>(null);
  const [survol, setSurvol] = useState<{ numero: string; moitie: MoitieSurvolee } | null>(null);

  /*
    LE DÉFILEMENT PENDANT LE GLISSEMENT.

    *Signalé par Chris le 23/09/2026 : « quand on arrive en bas de la page cela ne scrolle
    pas pour descendre et lâcher le chapitre plus bas ».* Le glisser-déposer natif fige la
    molette et les barres de défilement, donc tout ce qui n'est pas déjà à l'écran était
    hors d'atteinte — sur un déboursé qui descend à plus de 3 800 px, c'est-à-dire presque
    tout.

    Trois références plutôt que de l'état : la boucle tourne à la fréquence de l'écran et
    ne doit provoquer un rendu que lorsque la cible CHANGE réellement.
  */
  const curseur = useRef<{ x: number; y: number } | null>(null);
  const animation = useRef<number | null>(null);
  const derniereCible = useRef<string | null>(null);

  const arreterLeDefilement = (): void => {
    if (animation.current !== null) cancelAnimationFrame(animation.current);
    animation.current = null;
    curseur.current = null;
    derniereCible.current = null;
  };

  // Une boucle laissée tourner après un démontage continuerait de faire défiler une page
  // qui n'affiche plus le déboursé.
  useEffect(() => arreterLeDefilement, []);

  const demarrerLeDefilement = (bloc: string): void => {
    const racines = depo.racines;
    const avancer = (): void => {
      animation.current = requestAnimationFrame(avancer);
      const position = curseur.current;
      if (position === null) return;

      const pas = vitesseDeDefilement(position.y, window.innerHeight);
      if (pas === 0) return;
      window.scrollBy(0, pas);

      /*
        **La page a bougé sous un curseur immobile.**

        Sans ce recalcul, le trait d'insertion resterait sur la ligne survolée avant le
        défilement, et le bloc se poserait à un endroit que plus rien n'indiquait à
        l'écran — le pire des deux, puisque le geste aurait eu l'air juste. On repart donc
        de ce qui est RÉELLEMENT sous le curseur, et non du dernier événement reçu.
      */
      const dessous = document.elementFromPoint(position.x, position.y);
      const ligne = dessous?.closest<HTMLElement>("tr[data-poste]") ?? null;
      const numero = ligne?.dataset.poste;
      if (ligne === null || numero === undefined) return;

      const moitie = moitieSurvolee({ clientY: position.y }, ligne);
      const pose = poserLeBloc(racines, bloc, numero, moitie);
      const cible = pose === null ? null : `${numero}|${moitie}`;
      if (cible === derniereCible.current) return;
      derniereCible.current = cible;
      setSurvol(pose === null ? null : { numero, moitie });
    };
    animation.current = requestAnimationFrame(avancer);
  };

  const deplacement: ContexteDeplacement = {
    actif: modifiable && entete.type === "travail" && onDeplacer !== undefined,
    glisse,
    survol,
    demarrer: (numero) => {
      setGlisse(numero);
      demarrerLeDefilement(numero);
    },
    survoler: (numero, moitie, position) => {
      curseur.current = position;
      // Un survol qui ne mène à aucun dépôt possible n'allume rien : sans ce filtre, le
      // trait d'insertion inviterait à lâcher un bloc dans son propre contenu.
      const pose = poserLeBloc(depo.racines, glisse ?? "", numero, moitie);
      derniereCible.current = pose === null ? null : `${numero}|${moitie}`;
      setSurvol(pose === null ? null : { numero, moitie });
    },
    deposer: (numero, moitie) => {
      const bloc = glisse;
      arreterLeDefilement();
      setGlisse(null);
      setSurvol(null);
      if (bloc === null || onDeplacer === undefined) return;
      const pose = poserLeBloc(depo.racines, bloc, numero, moitie);
      if (pose !== null) onDeplacer(bloc, pose);
    },
    terminer: () => {
      arreterLeDefilement();
      setGlisse(null);
      setSurvol(null);
    },
  };

  const indexReference = reference === null ? null : indexerPostes(reference.racines);
  const ordonnancement =
    reference === null ? null : variationsDOrdonnancement(depo.racines, reference.racines);

  /**
   * Les tâches sans famille de travaux, et le chemin pour y arriver.
   *
   * L'ensemble contient les tâches **et leurs chapitres parents** : filtrer sur les
   * seules tâches les ferait disparaître avec leur branche, puisqu'un chapitre
   * masqué emporte ses enfants. C'est exactement le genre de filtre qui affiche
   * « rien à voir » alors qu'il y a treize postes à traiter.
   */
  const [filtreSansFamille, setFiltreSansFamille] = useState(false);
  const sansFamille = numerosSansFamille(depo.racines);
  const aMontrer = filtreSansFamille ? new Set(sansFamille) : null;

  /**
   * Les lignes « du groupe » dont la grille tarifaire ne donne aucun prix.
   *
   * Rechargé à chaque nouvelle vue du déboursé : désigner une carrière change ce compte, et
   * un compteur qui reste sur sa valeur d'avant est pire que pas de compteur. `depo` change
   * de référence après chaque écriture, c'est donc lui qui déclenche.
   *
   * Une panne de cette lecture ne casse rien : la map reste vide et le signalement disparaît.
   * L'inverse — vider l'écran parce qu'un signalement n'a pas pu se charger — serait absurde.
   */
  /**
   * Les matériaux de la grille tarifaire, pour la liste déroulante d'ajout.
   *
   * Chargés **une fois**, et non à chaque vue du déboursé comme `sansTarif` : c'est un
   * référentiel, il ne change pas pendant qu'on travaille un chantier — et l'administrer se
   * fait sur un autre écran, donc en rechargeant celui-ci.
   *
   * Lus ici plutôt que reçus en props : `App` charge déjà les carrières pour toute
   * l'application, et une liste qui ne sert qu'à ce formulaire n'a pas à peser sur l'ouverture
   * des autres écrans. Une panne de cette lecture laisse la liste vide, donc le groupe absent :
   * l'écran d'avant, exactement.
   *
   * Seuls les **actifs** sont proposés, comme les ressources génériques : un matériau retiré de
   * la grille ne s'ajoute plus, mais les lignes qui le portent déjà ne bougent pas.
   */
  const [articlesTarif, setArticlesTarif] = useState<readonly ArticleTarif[]>([]);
  useEffect(() => {
    let abandonne = false;
    chargerGrille()
      .then(({ articles }) => {
        if (!abandonne) setArticlesTarif(articles.filter((a) => a.actif));
      })
      .catch(() => {
        if (!abandonne) setArticlesTarif([]);
      });
    return () => {
      abandonne = true;
    };
  }, []);

  const [sansTarif, setSansTarif] = useState<ReadonlyMap<string, MotifSansTarif>>(new Map());
  useEffect(() => {
    let abandonne = false;
    chargerTarifsManquants(entete.id)
      .then(({ lignes }) => {
        if (abandonne) return;
        setSansTarif(new Map(lignes.map((l) => [`${l.poste}|${String(l.rang)}`, l.motif])));
      })
      .catch(() => {
        if (!abandonne) setSansTarif(new Map());
      });
    return () => {
      abandonne = true;
    };
  }, [entete.id, depo]);

  return (
    <>
      <Totaux
        depo={depo}
        entete={entete}
        reference={reference}
        libelleReference={libelleReference}
      />

      <ParFamille depo={depo} />

      <Besoins depo={depo} reference={reference} />

      <div className="barre-outils">
        {/* Le filtre des tâches sans famille — le contrôle bloquant du §4.3 ne se
            satisfait pas d'un compteur : il faut pouvoir aller aux manquantes.
            Le bouton disparaît quand il n'y en a plus, plutôt que de rester grisé :
            un filtre qui ne filtre rien n'a rien à dire. */}
        {sansFamille.length > 0 && (
          <button
            type="button"
            className={filtreSansFamille ? "actif" : undefined}
            onClick={() => {
              setFiltreSansFamille(!filtreSansFamille);
              // Déplier le chemin des postes concernés : filtrer sans ouvrir
              // montrerait une liste vide sur un arbre replié.
              if (!filtreSansFamille) setOuverts(new Set(tousLesNumeros(depo.racines)));
            }}
          >
            {filtreSansFamille ? "Voir tout" : `Voir les ${sansFamille.length} sans famille`}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setOuverts(new Set(tousLesNumeros(depo.racines)));
          }}
        >
          Tout déplier
        </button>
        <button
          type="button"
          onClick={() => {
            setOuverts(new Set());
          }}
        >
          Tout replier
        </button>
        <button
          type="button"
          onClick={() => {
            setOuverts(new Set(numerosChapitres(depo.racines)));
          }}
        >
          Chapitres seuls
        </button>
        {modifiable ? (
          <span className="note">
            Déplier un ouvrage pour modifier ses quantités et ses prix. Entrée valide, Échap
            annule.
          </span>
        ) : (
          <span className="note">
            Version figée — lecture seule. Choisir la copie de travail pour modifier.
          </span>
        )}
        {depo.totaux.lignesColonneMontantDivergente > 0 && (
          <span className="note">
            {depo.totaux.lignesColonneMontantDivergente} ligne(s) dont la colonne « coût total » du
            fichier ne vaut pas temps&nbsp;×&nbsp;prix — connu, cette colonne n'est jamais utilisée
            pour calculer.
          </span>
        )}
        {/*
          Le compteur des lignes du groupe sans tarif. Il ne peut pas se taire : une ligne
          annoncée « du groupe » qui est restée au prix du fournisseur se lit comme chiffrée au
          tarif du groupe, et rien d'autre à l'écran ne le dit. C'est la leçon des absences non
          rattachées du 16/08 — un tableau qui ne signale pas ce qu'il n'a pas trouvé se lit
          « tout va bien ».
        */}
        {sansTarif.size > 0 && (
          <span className="alerte">
            {sansTarif.size} ligne(s) du groupe <strong>chiffrée(s) à 0</strong> : la grille
            tarifaire ne donne aucun prix. Renseigner les prix au référentiel puis redésigner la
            carrière — déplier l'ouvrage pour voir lesquelles et pourquoi.
          </span>
        )}
      </div>

      <table className="depo">
        <ColonnesCalees />
        <thead>
          <tr>
            <th
              className="col-retenu"
              title="Compté dans le chiffrage. Décocher écarte le poste des charges ET du chiffre d'affaires."
            >
              ✓
            </th>
            <th className="col-ligne" title="Numéro de ligne dans le classeur d'origine">
              Ligne
            </th>
            <th className="col-numero">N°</th>
            <th>Désignation</th>
            <th
              className="col-groupe"
              title="Famille de travaux (G1-G10). Posée sur un chapitre, elle est héritée par ses ouvrages."
            >
              Groupe
            </th>
            <th>Unité</th>
            <th className="col-nombre" title="Somme des lignes dont l'unité est l'heure">
              Heures
            </th>
            <th className="col-nombre col-devis">{libelleReference ?? "Devis"}</th>
            {/* La quantité de vente du poste suit la même colonne que la quantité des
                lignes de ressources : les deux tableaux partagent une grille, donc
                déplacer l'une déplace l'autre. Elle ne se saisit pas ici — c'est le
                classeur qui la donne —, et elle ne porte donc pas le fond des colonnes
                de saisie. */}
            <th className="col-nombre">Quantité</th>
            <th className="col-nombre col-devis" title="Prix de vente au devis figé">
              Vente devis
            </th>
            <th className="col-nombre">Contre-étude</th>
            <th className="col-nombre" title="Prix de vente sur le périmètre retenu">
              Vente
            </th>
            <th
              className="col-nombre"
              title="Variation due au chiffrage : prix, quantités, heures, postes écartés. Les déplacements de blocs n'y entrent pas."
            >
              Δ
            </th>
            <th
              className="col-nombre"
              title="Variation due au déplacement des blocs : ce que le chapitre a reçu ou cédé, au prix du devis figé. La somme sur les chapitres de premier niveau vaut zéro."
            >
              Var. ordonnan&shy;cement
            </th>
            <th className="col-nombre">Annoncé</th>
            <th className="col-nombre">Écart fichier</th>
          </tr>
        </thead>
        <tbody>
          {depo.racines.map((noeud) => (
            <LigneNoeud
              key={noeud.numero}
              noeud={noeud}
              reference={indexReference}
              ordonnancement={ordonnancement}
              profondeur={0}
              ouverts={ouverts}
              basculer={basculer}
              modifiable={modifiable}
              enAttente={enAttente}
              generiques={generiques}
              articlesTarif={articlesTarif}
              groupes={groupes}
              carrieres={carrieres}
              sansTarif={sansTarif}
              stock={stock}
              aMontrer={aMontrer}
              deplacement={deplacement}
              onModifier={onModifier}
            />
          ))}
          {/*
            **Les rappels et le total de l'ordonnancement** *(tranché par Chris, 24/09/2026)*.

            Un poste de premier niveau du devis rangé dans un autre apporte tout son contenu à
            son nouveau parent sans qu'aucune ligne de premier niveau ne le perde : il laisse ici
            une ligne grisée qui porte la contrepartie. Le total se lit en dessous, et il fait
            zéro — calculé, pas supposé. Masqués sous un filtre : ils ne parlent que de l'arbre
            entier.
          */}
          {ordonnancement !== null &&
            aMontrer === null &&
            ordonnancement.rappels.map((rappel) => (
              <tr key={`rappel-${rappel.numero}`} className="rappel-ordonnancement">
                <td colSpan={2} />
                <td>{rappel.numero}</td>
                <td colSpan={9}>
                  {rappel.designation}
                  <span className="note">
                    {" — "}
                    {rappel.sousLe === null
                      ? "ne figure plus au premier niveau"
                      : `rangé sous ${rappel.sousLe}`}
                  </span>
                </td>
                <td />
                <td className="col-nombre">
                  <Delta valeur={rappel.variation} neutre />
                </td>
                <td colSpan={2} />
              </tr>
            ))}
          {ordonnancement !== null && aMontrer === null && (
            <tr className="total-ordonnancement">
              <td colSpan={3} />
              <td colSpan={9}>Total des variations d&apos;ordonnancement, premier niveau</td>
              <td />
              <td className="col-nombre">
                {/* En chiffres et non « = » : c'est le zéro qu'on vient lire ici. */}
                <strong>{formaterMontant(ordonnancement.total.toFixed(2))}</strong>
              </td>
              <td colSpan={2} />
            </tr>
          )}
        </tbody>
      </table>

      {/*
        **Créer un chapitre absent du devis** *(demande de Chris, 21/08/2026 — point ouvert
        n°24 du PRD, tranché)*.

        Sous le tableau et non au-dessus : le chapitre créé se place à la suite du dernier, et
        le bouton est donc là où la trame va apparaître. Un clic = un chapitre et sa
        sous-section ; le clic suivant en donne un autre, numéroté à la suite.
      */}
      {modifiable && (
        <div className="ajout ajout--chapitre">
          <button
            type="button"
            disabled={enAttente !== null}
            onClick={() => {
              onModifier({ poste: "", rang: 0, creerChapitre: true });
            }}
          >
            Ajouter un chapitre
          </button>
          <span className="precision">
            Un chapitre vierge à la suite du dernier, avec son sous-chapitre. Son coût entre au
            déboursé recalculé et au planning ; rien ne s'ajoute au prix de vente.
          </span>
        </div>
      )}
    </>
  );
}
